import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { FileText, ExternalLink, Image, Calendar, Building, Mail, Phone, MapPin, Globe, AlertTriangle, Award, Trash2, ChevronDown, ChevronUp, Clock, Search, ChevronLeft, ChevronRight, Eye, Download, File, CheckSquare, Square, X, Info, CheckCircle, RotateCw, Loader2, ShieldCheck, History, DollarSign, Facebook, Instagram, Linkedin, Youtube, Twitter, MessageCircle, Music } from "lucide-react";
import ReviewButton from "../components/ui/review-button";
import MerchantHistoryPage from "./MerchantHistoryPage";
import { isPlatformAdmin, isDemoWorkspace, demoAPI, getUser } from "../lib/auth";
import { publicDisplayUrl } from "../lib/utils";

const API_BASE = "";

function demoReportURL(raw) {
  const value = String(raw || '').trim();
  if (!value) return value;
  if (/^https?:\/\//i.test(value)) return value;
  return `https://${value.replace(/^\/+/, '')}`;
}

function crc32(data) {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i += 1) {
    crc ^= data[i];
    for (let bit = 0; bit < 8; bit += 1) {
      crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
    }
  }
  return (~crc) >>> 0;
}

function buildStoredZip(files) {
  const encoder = new TextEncoder();
  const locals = [];
  const central = [];
  let offset = 0;
  files.forEach((file) => {
    const nameBytes = encoder.encode(file.name);
    const data = file.data;
    const crc = crc32(data);
    const local = new Uint8Array(30 + nameBytes.length);
    const view = new DataView(local.buffer);
    view.setUint32(0, 0x04034b50, true);
    view.setUint16(4, 20, true);
    view.setUint16(8, 0, true);
    view.setUint32(14, crc, true);
    view.setUint32(18, data.length, true);
    view.setUint32(22, data.length, true);
    view.setUint16(26, nameBytes.length, true);
    local.set(nameBytes, 30);
    locals.push(local, data);
    const cen = new Uint8Array(46 + nameBytes.length);
    const cv = new DataView(cen.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, data.length, true);
    cv.setUint32(24, data.length, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint32(42, offset, true);
    cen.set(nameBytes, 46);
    central.push(cen);
    offset += local.length + data.length;
  });
  const centralSize = central.reduce((sum, part) => sum + part.length, 0);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, files.length, true);
  ev.setUint16(10, files.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);
  return new Blob([...locals, ...central, end], { type: 'application/zip' });
}

function triggerBrowserDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

async function fetchGeneratedReport(merchantUrl, format) {
  const generateResponse = await fetch('/api/v1/enterprise/reports/generate/', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      tenant_id: null,
      report_type: 'merchant_processing',
      format,
      merchant_url: merchantUrl,
      send_email: false,
    }),
  });
  if (!generateResponse.ok) {
    const err = await generateResponse.json().catch(() => ({}));
    throw new Error(err.message || err.error || 'Failed to generate report');
  }
  const { report_id, status } = await generateResponse.json();
  const pull = async () => {
    const downloadResponse = await fetch(`/api/v1/enterprise/reports/${report_id}/download/`);
    if (!downloadResponse.ok) throw new Error('Failed to download report');
    const contentDisposition = downloadResponse.headers.get('Content-Disposition');
    const ext = format === 'excel' ? 'xlsx' : format;
    let filename = `${String(merchantUrl).replace(/[^a-z0-9]/gi, '_')}_report.${ext}`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=['"]?([^'"\n;]+)['"]?/);
      if (match && match[1]) filename = match[1].trim();
    }
    return { filename, blob: await downloadResponse.blob() };
  };
  if (status === 'completed') return pull();
  for (let attempt = 0; attempt < 150; attempt += 1) {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    const statusResponse = await fetch(`/api/v1/enterprise/reports/${report_id}/status/`);
    if (!statusResponse.ok) continue;
    const statusData = await statusResponse.json();
    if (statusData.status === 'completed') return pull();
    if (statusData.status === 'failed') throw new Error(statusData.error || 'Report generation failed');
  }
  throw new Error('Report is taking longer than expected');
}
const ORANGE_BTN =
  "bg-[#F5A623] text-[#1B365D] border-[#E8941E] hover:bg-[#E8941E] hover:text-[#1B365D]";
const ORANGE_TAB_ACTIVE = "border-[#F5A623] text-[#1B365D]";

function classificationSourceLabel(source) {
  const s = String(source || '').trim();
  if (!s) return '';
  const key = s.toLowerCase();
  if (key.includes('brightdata') || key.includes('google ai mode')) return '';
  if (key.includes('ats_293_backup')) return 'Backup Crawler';
  if (key === 'backup_crawl' || key.includes('backup')) return 'Backup Crawler';
  if (key === 'mistral 7b' || key === 'mistral' || key === 'primary' || key === 'core crawler') return 'Core Crawler';
  if (key.includes('spacy')) return 'spaCy';
  if (key.includes('archived')) return 'Archived history';
  if (key.includes('293') || key.includes('ats')) return '';
  return s;
}

function needsMccVerification(mcc) {
  if (!mcc || typeof mcc !== 'object') return false;
  if (mcc.needs_manual_verification === true || mcc.needs_manual_verification === 'true') return true;
  return /manual verification required/i.test(String(mcc.mcc_validation_note || ''));
}

function stripAtsLibraryText(text) {
  return String(text || '')
    .replace(/Google AI Mode/gi, 'PayLegit AI')
    .replace(/Google AI/gi, 'PayLegit AI')
    .replace(/MCC assigned from ATS 293 backup because PayLegit AI returned no code\s*[—-]\s*manual verification required\.?/gi, '')
    .replace(/MCC description filled from ATS 293 library\s*[—-]\s*manual verification required\.?/gi, '')
    .replace(/\s*Matched ATS 293 library\s*\([^)]*\)\.?/gi, '')
    .replace(/MCC NAME:\s*[^.\n]+\.\s*/gi, '')
    .replace(/\s*·\s*ATS 293/gi, '')
    .replace(/\s*filled from the ATS 293 library/gi, '')
    .replace(/\s*from the ATS 293 library/gi, '')
    .replace(/\bATS 293 library\b/gi, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function sanitizeMccClassification(mcc) {
  if (!mcc || typeof mcc !== 'object') return mcc;
  const next = { ...mcc };
  const src = String(next.source || '').toLowerCase();
  if (src.includes('ats_293_backup')) {
    next.source = 'ats_293_backup';
  } else if (
    src.includes('brightdata') ||
    src.includes('google ai mode') ||
    src.includes('293') ||
    /\bats\b/.test(src)
  ) {
    next.source = '';
  }
  [
    'analysis_summary',
    'reasoning',
    'primary_mcc_reasoning',
    'secondary_mcc_reasoning',
    'primary_mcc_description',
    'secondary_mcc_description',
    'primary_mcc_category',
    'secondary_mcc_category',
    'mcc_validation_note',
  ].forEach((key) => {
    if (next[key]) next[key] = stripAtsLibraryText(next[key]);
  });
  return next;
}

function sanitizeMerchant(merchant) {
  if (!merchant || typeof merchant !== 'object') return merchant;
  const products = Array.isArray(merchant.products_detailed)
    ? merchant.products_detailed.map((p) =>
        p && typeof p === "object"
          ? {
              ...p,
              source_url: publicDisplayUrl(p.source_url),
              page_url: publicDisplayUrl(p.page_url),
            }
          : p
      )
    : merchant.products_detailed;
  return {
    ...merchant,
    mcc_classification: sanitizeMccClassification(merchant.mcc_classification),
    products_detailed: products,
  };
}
const POLICY_FLAG_ORDER = [
  "terms_of_service",
  "privacy_policy",
  "shipping_policy",
  "refund_policy",
  "return_policy",
  "cancellation_policy",
];

const POLICY_FLAG_LABELS = {
  terms_of_service: "Terms of Service",
  privacy_policy: "Privacy",
  shipping_policy: "Shipping",
  refund_policy: "Refund",
  return_policy: "Return",
  cancellation_policy: "Cancellation",
};

/** Normalize merchant.policy_flags into a stable list for green/red badges. */
function getPolicyFlagEntries(policyFlags) {
  const src = policyFlags && typeof policyFlags === "object" ? policyFlags : {};
  const entries = POLICY_FLAG_ORDER.map((key) => {
    const value = src[key] || {};
    return {
      key,
      label: POLICY_FLAG_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
      exists: Boolean(value?.exists),
      url: value?.url || "",
    };
  });
  // Include any extra flags (e.g. cancellation) after the core set
  Object.keys(src).forEach((key) => {
    if (POLICY_FLAG_ORDER.includes(key)) return;
    const value = src[key] || {};
    entries.push({
      key,
      label: POLICY_FLAG_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
      exists: Boolean(value?.exists),
      url: value?.url || "",
    });
  });
  return entries;
}

function isValidAboutText(text) {
  if (!text || typeof text !== 'string') return false;
  const trimmed = text.trim();
  if (trimmed.length === 0) return false;
  const lower = trimmed.toLowerCase();
  if (lower.includes('about information extraction failed')) return false;
  if (lower.includes('extraction failed')) return false;
  if (lower.includes('company description not available')) return false;
  return true;
}

function cleanAboutDisplayText(text) {
  return stripAtsLibraryText(String(text || ''))
    .replace(/here is the structured breakdown for[^.:\n]*[:.]?\s*/gi, '')
    .replace(/here is the requested information[^.]*\.?\s*/gi, '')
    .replace(/payment processors typically classify[^.]*\.?\s*/gi, '')
    .replace(/\*{0,2}mcc\s*\d{3,4}\*{0,2}/gi, '')
    .replace(/\bmcc\s*[:#]?\s*\d{3,4}\b/gi, '')
    .replace(/\b(pxp\.io|altopay|visa|mastercard)\s*\+?\d*\b/gi, '')
    .replace(/\b[a-z0-9.-]+\.(?:io|com|net|org)\s*\+?\d+\b/gi, '')
    .replace(/(?:^|[\s,;])(?:[a-z]{1,3})\s*\+\d+(?=[\s,;.]|$)/gi, ' ')
    .replace(/(?:^|[\s,;])\+\d+(?=[\s,;.]|$)/g, ' ')
    .replace(/\[\d+\]/g, '')
    .replace(/about us\s*\/\s*our story\s*:?\s*/gi, '')
    .replace(/sources?\s*:\s*[^.]*\.?\s*/gi, '')
    .replace(/\*{1,2}/g, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/(?:,\s*){2,}/g, ', ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function resolveMerchantAboutText(merchant) {
  const candidates = [
    merchant?.about_text,
    merchant?.about_us_raw?.company_description,
    merchant?.about_us_raw?.about_page_meta,
    merchant?.about_us_raw?.homepage_meta,
  ];
  for (const raw of candidates) {
    const cleaned = cleanAboutDisplayText(raw);
    if (isValidAboutText(cleaned) && cleaned.length >= 40) return cleaned;
  }

  const summary = cleanAboutDisplayText(merchant?.mcc_classification?.analysis_summary || '');
  const beforePrimary = summary.split(/\n+\s*Primary MCC\b/i)[0].trim();
  if (isValidAboutText(beforePrimary) && beforePrimary.length >= 40 && !/\bmcc\s*\d{3,4}\b/i.test(beforePrimary)) {
    return beforePrimary;
  }
  const beforeReasons = summary.split(/\n+\s*MCC reasons:/i)[0].trim();
  if (isValidAboutText(beforeReasons) && beforeReasons.length >= 40 && !/\bmcc\s*\d{3,4}\b/i.test(beforeReasons)) {
    return beforeReasons;
  }
  const reason = cleanAboutDisplayText(merchant?.mcc_classification?.primary_mcc_reasoning || '');
  if (isValidAboutText(reason) && reason.length >= 40 && !/\bmcc\s*\d{3,4}\b/i.test(reason)) {
    return reason;
  }
  return 'No about information available.';
}

/** True when crawled "name" is a page title, SKU, or HTTP/error shell — not a brand. */
function isJunkCompanyName(name) {
  const t = String(name || '').trim().toLowerCase().replace(/^[#*\s]+/, '');
  if (!t) return true;
  if (
    /\b(400|401|403|404|500|502|503)\b/.test(t) ||
    t.includes('bad request') ||
    t.includes('not found') ||
    t.includes('unauthorized') ||
    t.includes('forbidden') ||
    t.includes('internal server error') ||
    t.startsWith('error:') ||
    t.startsWith('error ')
  ) {
    return true;
  }
  const exact = new Set([
    'privacy statement', 'privacy policy', 'cookie policy', 'email protection',
    'sign in', 'log in', 'login', 'sign up', 'about us', 'contact us', 'home',
  ]);
  if (exact.has(t)) return true;
  if (
    t.startsWith('webinar') || t.startsWith('sign in') || t.startsWith('log in') ||
    t.startsWith('buy ') || t.startsWith('shop ') ||
    t.includes('privacy statement') || t.includes('email protection')
  ) {
    return true;
  }
  if (t.startsWith('about ') && t.split(/\s+/).length > 6) return true;
  if (/\b\d+\s*[nx]\s*x\s*\d+\s*(gm|g|kg|ml)\b/i.test(t)) return true;
  const words = t.split(/\s+/).filter(Boolean);
  if (words.length > 8 && !/\b(pvt|ltd|limited|inc|llc|llp)\b/.test(t)) return true;
  return false;
}

function formatNameCasing(name) {
  const t = String(name || '').trim();
  if (!t) return t;
  const letters = t.replace(/[^A-Za-z]/g, '');
  if (letters.length >= 6 && t === t.toUpperCase() && /[A-Z]/.test(t)) {
    const small = new Set(['of', 'and', 'the', 'for', 'in']);
    return t.toLowerCase().replace(/\b\w+/g, (word, offset) => {
      if (offset > 0 && small.has(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    });
  }
  return t;
}

/** Brand for UI: real company name, else domain label (accessequity.in → Accessequity). */
function displayCompanyName(companyName, domainOrUrl) {
  if (companyName && !isJunkCompanyName(companyName)) {
    return formatNameCasing(companyName);
  }
  const raw = String(domainOrUrl || companyName || '').trim();
  try {
    const host = raw.includes('://') ? new URL(raw.startsWith('http') ? raw : `https://${raw}`).hostname : raw;
    const label = host.replace(/^www\./i, '').split('.')[0];
    if (label) return label.charAt(0).toUpperCase() + label.slice(1);
  } catch (_) { /* fall through */ }
  return formatNameCasing(companyName || domainOrUrl || 'Unknown');
}

/** List/header: crawl input merchant name, else cleaned extracted name. */
function displayMerchantName(merchant) {
  const input = String(merchant?.merchant_name || '').trim();
  if (input && !isJunkCompanyName(input)) {
    return formatNameCasing(input);
  }
  return displayCompanyName(
    merchant?.company_name,
    merchant?.domain || merchant?.merchant_url || merchant?.id
  );
}

/** Display one or more phones from API (comma-separated); preserves +1, +91, and other country codes. */
function formatContactPhoneDisplay(phone) {
  if (!phone) return 'Not available';
  const parts = String(phone).split(/[,;|]/).map((p) => p.trim()).filter(Boolean);
  const formatted = parts.map((part) => {
    const digits = part.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }
    if (digits.length === 12 && digits.startsWith('91')) {
      const d = digits.slice(2);
      if (d.length === 10 && d[0] >= '6' && d[0] <= '9') {
        return `+91 ${d.slice(0, 5)} ${d.slice(5)}`;
      }
      return `+91 ${d}`;
    }
    if (part.startsWith('+') && digits.length >= 10 && digits.length <= 15) {
      return part;
    }
    if (digits.length === 10 && digits[0] >= '6' && digits[0] <= '9') {
      return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
    }
    if (digits.length >= 10 && digits.length <= 11) {
      return `+91 ${digits}`;
    }
    if (part.length >= 8) return part;
    return null;
  }).filter(Boolean);
  return formatted.length ? formatted.join(', ') : 'Not available';
}

/** Hide scraped HTML/nav/legal junk; only show a short postal address. */
function formatContactAddressDisplay(address) {
  const raw = String(address || '').trim().replace(/\s+/g, ' ');
  if (!raw) return 'Not available';
  const lower = raw.toLowerCase();
  const junkMarkers = ['<img', '</a>', '</nav>', '</div>', 'class=', 'src=', 'href=', 'javascript:', 'data-cfemail'];
  if (raw.includes('<') || raw.includes('>') || junkMarkers.some((m) => lower.includes(m))) {
    return 'Not available';
  }
  if (/https?:\/\/|www\./i.test(raw)) return 'Not available';
  const us = raw.match(/\d{1,6}\s+[A-Za-z0-9.'#\s,-]{6,90}?\b[A-Z]{2}\s+\d{5}(?:-\d{4})?/);
  if (us && us[0].length <= 160) return us[0].trim();
  const legal = /terms of service|privacy policy|click-wrap|legally required|we may modify|faqs|your continued use|electronic records/i;
  if (legal.test(raw) || raw.length > 180) return 'Not available';
  const hasIndicator =
    /\b\d{5,6}\b/.test(raw) ||
    /\d+\s+[\w\s]{2,40}(street|st\.?|road|rd\.?|avenue|ave\.?|blvd|lane|drive|way)/i.test(raw) ||
    /(suite|floor|building|tower|india|united states|california|street|road|office)/i.test(raw);
  return hasIndicator ? raw : 'Not available';
}

function currencyChipCodes(raw, max = 4) {
  const codes = [];
  String(raw || '').split(/[,;\s]+/).forEach((part) => {
    const code = part.trim().toUpperCase();
    if (/^[A-Z]{3}$/.test(code) && !codes.includes(code)) codes.push(code);
  });
  return { shown: codes.slice(0, max), extra: Math.max(0, codes.length - max) };
}

function CurrencyChips({ raw, className = '' }) {
  const { shown, extra } = currencyChipCodes(raw, 4);
  if (!shown.length) return null;
  return (
    <span className={`inline-flex flex-wrap items-center gap-1 ${className}`}>
      {shown.map((code) => (
        <span
          key={code}
          className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0 text-[10px] font-medium leading-4 text-amber-800"
        >
          {code}
        </span>
      ))}
      {extra > 0 ? <span className="text-[10px] text-amber-700">+{extra}</span> : null}
    </span>
  );
}

function formatCleanUrlLocation(loc) {
  if (!loc || typeof loc !== 'object') return '';
  const country = String(loc.country_name || loc.country_code || '')
    .replace(/United States of America/gi, 'United States')
    .trim();
  const parts = [loc.city, loc.region, country]
    .map((p) => String(p || '').trim())
    .filter(Boolean)
    .filter((p, i, arr) => arr.findIndex((x) => x.toLowerCase() === p.toLowerCase()) === i);
  if (parts.length) return parts.join(', ');
  const display = String(loc.display || '')
    .replace(/\s*\(\d{1,3}(?:\.\d{1,3}){3}\)\s*/g, '')
    .replace(/\s*[-·|]\s*ip2location(?:\.io)?/gi, '')
    .replace(/United States of America/gi, 'United States')
    .trim();
  return display;
}

function isRealSocialProfileUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return false;
  const lower = raw.toLowerCase();
  if (
    lower.includes('sharearticle') ||
    lower.includes('intent/tweet') ||
    lower.includes('intent/share') ||
    lower.includes('pin/create') ||
    lower.includes('sharer.php') ||
    lower.includes('/share?')
  ) {
    return false;
  }
  try {
    const u = new URL(raw.startsWith('http') ? raw : `https://${raw}`);
    const path = (u.pathname || '').replace(/\/+$/, '');
    return path.length > 1;
  } catch (_) {
    return false;
  }
}

function socialLinksForIcons(socialLinks) {
  if (!socialLinks || typeof socialLinks !== 'object') return [];
  const catalog = [
    { key: 'facebook', label: 'Facebook', Icon: Facebook, className: 'text-[#1877F2] hover:bg-[#1877F2]/10' },
    { key: 'instagram', label: 'Instagram', Icon: Instagram, className: 'text-[#E4405F] hover:bg-[#E4405F]/10' },
    { key: 'linkedin', label: 'LinkedIn', Icon: Linkedin, className: 'text-[#0A66C2] hover:bg-[#0A66C2]/10' },
    { key: 'youtube', label: 'YouTube', Icon: Youtube, className: 'text-[#FF0000] hover:bg-[#FF0000]/10' },
    { key: 'twitter', label: 'X / Twitter', Icon: Twitter, className: 'text-slate-800 hover:bg-slate-100' },
    { key: 'pinterest', label: 'Pinterest', Icon: Globe, className: 'text-[#BD081C] hover:bg-[#BD081C]/10' },
    { key: 'tiktok', label: 'TikTok', Icon: Music, className: 'text-slate-900 hover:bg-slate-100' },
    { key: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle, className: 'text-[#25D366] hover:bg-[#25D366]/10' },
  ];
  return catalog
    .map((item) => ({ ...item, url: socialLinks[item.key] }))
    .filter((item) => isRealSocialProfileUrl(item.url));
}

// URL List Card Component with expandable list
function URLListCard({ count, label, color, urls = [] }) {
  const [expanded, setExpanded] = useState(false);
  
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    purple: "bg-purple-50 text-purple-600",
    orange: "bg-orange-50 text-orange-600",
    red: "bg-red-50 text-red-600",
    teal: "bg-teal-50 text-teal-600",
  };
  
  const hasURLs = urls && urls.length > 0;
  
  return (
    <div className="space-y-1 sm:space-y-2">
      <div 
        className={`text-center p-2 sm:p-3 md:p-4 rounded-lg cursor-pointer transition-all ${colorClasses[color] || colorClasses.blue} ${hasURLs ? 'hover:shadow-md' : ''}`}
        onClick={() => hasURLs && setExpanded(!expanded)}
      >
        <div className="flex items-center justify-center space-x-1 sm:space-x-2">
          <p className={`text-base sm:text-lg md:text-2xl font-bold ${color ? `text-${color}-600` : 'text-blue-600'}`}>{count}</p>
          {hasURLs && (
            <span className="text-[10px] sm:text-xs">{expanded ? '▼' : '▶'}</span>
          )}
        </div>
        <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground mt-0.5 sm:mt-1">{label}</p>
      </div>
      
      {expanded && hasURLs && (
        <div className="p-3 bg-white border rounded-lg shadow-sm max-h-60 overflow-y-auto">
          <p className="text-xs font-semibold mb-2">{label} URLs ({urls.length}):</p>
          <ul className="space-y-1">
            {urls.map((url, index) => (
              <li key={index} className="text-xs">
                <a 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline flex items-center space-x-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  <span className="truncate">{url}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const listView = searchParams.get("view") === "failed" ? "failed" : "successful";
  const merchantParam = searchParams.get("merchant");
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const openedMerchantRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [showMetaForProduct, setShowMetaForProduct] = useState({});
  const [activeTab, setActiveTab] = useState("overview");
  const [deleting, setDeleting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [expandedSummary, setExpandedSummary] = useState(false);
  const [imageZoom, setImageZoom] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [merchants, setMerchants] = useState([]);
  const [failedMerchants, setFailedMerchants] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [failedCurrentPage, setFailedCurrentPage] = useState(1);
  const [dateFilter, setDateFilter] = useState('all'); // 'today' | '7days' | '30days' | 'all' | 'custom'
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [reportsMainTab, setReportsMainTab] = useState('merchants'); // 'merchants' | 'history'
  const [generatingReports, setGeneratingReports] = useState(new Set());

  const [selectedMerchantsForBulk, setSelectedMerchantsForBulk] = useState([]);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [mappingToDemo, setMappingToDemo] = useState(false);
  const [hidingFromDemo, setHidingFromDemo] = useState(false);
  const platformAdmin = isPlatformAdmin();
  const demoWorkspace = isDemoWorkspace();
  const canHideInDemo = demoWorkspace && (platformAdmin || getUser()?.role === 'tenant_admin');
  const [selectAllPages, setSelectAllPages] = useState(false);
  const [showCombinedData, setShowCombinedData] = useState(false);

  /** Website authenticity — on-demand report using existing merchant/parsed data (Django analyse API) */
  const [authenticityLoading, setAuthenticityLoading] = useState(false);
  const [authenticityError, setAuthenticityError] = useState(null);
  const [authenticityResult, setAuthenticityResult] = useState(null);
  const [authenticityPdfLoading, setAuthenticityPdfLoading] = useState(false);
  const [currencyRefreshing, setCurrencyRefreshing] = useState(false);
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);

  // Backup crawl state
  const [backupCrawling, setBackupCrawling] = useState(false);
  const [backupCrawlBatchId, setBackupCrawlBatchId] = useState(null);
  const [backupCrawlStatus, setBackupCrawlStatus] = useState(null);
  const [snackbars, setSnackbars] = useState([]);
  const itemsPerPage = 15;

  const getTenantScopeParam = () => {
    const viewingTenantId = localStorage.getItem('viewing_tenant_id');
    return viewingTenantId ? `?tenant_id=${encodeURIComponent(viewingTenantId)}` : '';
  };

  const patchSearchParams = (mutate) => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    setSearchParams(next, { replace: true });
  };

  const openMerchantById = (id) => {
    if (!id) return;
    patchSearchParams((n) => n.set('merchant', id));
  };

  const closeMerchantView = () => {
    openedMerchantRef.current = null;
    patchSearchParams((n) => n.delete('merchant'));
    setSelectedMerchant(null);
    setActiveTab('overview');
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  const normalizeMerchantUrl = (url) => {
    if (!url) return '';
    return url.toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/$/, '');
  };

  /** When MCC was last classified — not crawl/parsed_at. */
  const getMerchantClassifiedAt = (merchant) => {
    const raw = merchant?.classified_at
      || merchant?.mcc_classification?.analyzed_at
      || merchant?.last_updated;
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  /** Inclusive custom/preset date window for list filtering. Returns { from, to } or null. */
  const getDateFilterBounds = () => {
    const now = new Date();
    if (dateFilter === 'custom' || (customDateFrom || customDateTo)) {
      const from = customDateFrom ? new Date(`${customDateFrom}T00:00:00`) : null;
      const to = customDateTo ? new Date(`${customDateTo}T23:59:59.999`) : null;
      if (!from && !to) return null;
      return { from, to };
    }
    if (dateFilter === 'today') {
      const from = new Date(now); from.setHours(0, 0, 0, 0);
      return { from, to: null };
    }
    if (dateFilter === '7days') {
      const from = new Date(now); from.setDate(from.getDate() - 7);
      return { from, to: null };
    }
    if (dateFilter === '30days') {
      const from = new Date(now); from.setDate(from.getDate() - 30);
      return { from, to: null };
    }
    return null;
  };

  const merchantInDateBounds = (merchant) => {
    const bounds = getDateFilterBounds();
    if (!bounds) return true;
    const classified = getMerchantClassifiedAt(merchant);
    if (!classified) return false;
    if (bounds.from && classified < bounds.from) return false;
    if (bounds.to && classified > bounds.to) return false;
    return true;
  };

  const merchantMatchesSearch = (merchant, rawSearch) => {
    const q = normalizeMerchantUrl(rawSearch.trim());
    if (!q) return true;
    const companyName = normalizeMerchantUrl(merchant.company_name || '');
    const merchantName = normalizeMerchantUrl(merchant.merchant_name || '');
    const domain = normalizeMerchantUrl(merchant.domain || merchant.merchant_url || merchant.id || '');
    const merchantUrl = normalizeMerchantUrl(merchant.merchant_url || merchant.id || '');
    const raw = rawSearch.toLowerCase();
    return companyName.includes(q) || merchantName.includes(q) || domain.includes(q) || merchantUrl.includes(q)
      || (merchant.company_name || '').toLowerCase().includes(raw)
      || (merchant.merchant_name || '').toLowerCase().includes(raw);
  };

  // Toast notification helper
  const showSnackbar = (message, severity = 'info', action = null) => {
    const id = Date.now() + Math.random();
    setSnackbars(prev => [...prev, { id, message, severity, action }]);
    setTimeout(() => setSnackbars(prev => prev.filter(s => s.id !== id)), 6000);
  };

  useEffect(() => {
    fetchMerchants({ silent: Boolean(merchantParam) });
  }, []);

  useEffect(() => {
    if (!merchantParam) {
      openedMerchantRef.current = null;
      setSelectedMerchant(null);
      setDetailsLoading(false);
      return;
    }
    if (openedMerchantRef.current === merchantParam && selectedMerchant) {
      return;
    }
    openedMerchantRef.current = merchantParam;
    fetchMerchantDetails(merchantParam);
  }, [merchantParam]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && selectedScreenshot) {
        setSelectedScreenshot(null);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [selectedScreenshot]);

  const fetchMerchants = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const tenantScope = getTenantScopeParam();
      // High limit so review_status is accurate for the full client-side list
      const reviewsUrl = tenantScope
        ? `${API_BASE}/api/v1/workflow/reviews/${tenantScope}&limit=5000`
        : `${API_BASE}/api/v1/workflow/reviews/?limit=5000`;
      const failedMerchantsUrl = tenantScope
        ? `${API_BASE}/api/merchants/manual-retry-queue${tenantScope}`
        : `${API_BASE}/api/merchants/manual-retry-queue`;

      // First paint: merchants + reviews only. Failed-queue cleanup is heavy and
      // must not block the Reports table spinner (it runs in the background).
      const [merchantsRes, reviewsRes] = await Promise.all([
        fetch(`${API_BASE}/api/merchants${tenantScope}`),
        fetch(reviewsUrl),
      ]);

      if (merchantsRes.ok) {
        const merchantsData = await merchantsRes.json();
        let merchantsList = merchantsData.merchants || [];

        // Paylegit Demo: own ∪ mapped − hidden (merchant_url report ids)
        if (isDemoWorkspace()) {
          try {
            const overlay = await demoAPI.overlay();
            const hiddenSet = new Set((overlay.hidden_urls || []).map(normalizeMerchantUrl));
            const mappedUrls = overlay.mapped_urls || [];
            merchantsList = merchantsList.filter((m) => {
              const key = normalizeMerchantUrl(m.id || m.merchant_url || m.domain);
              return key && !hiddenSet.has(key);
            });
            const present = new Set(
              merchantsList.map((m) => normalizeMerchantUrl(m.id || m.merchant_url || m.domain))
            );
            for (const rawUrl of mappedUrls) {
              const key = normalizeMerchantUrl(rawUrl);
              if (!key || present.has(key) || hiddenSet.has(key)) continue;
              try {
                // Fetch mapped merchant without demo tenant filter so source-tenant rows resolve
                const detailRes = await fetch(
                  `${API_BASE}/api/merchants/${encodeURIComponent(demoReportURL(rawUrl))}`
                );
                if (!detailRes.ok) continue;
                const detail = await detailRes.json();
                const merchant = detail.merchant || detail;
                if (merchant && (merchant.id || merchant.merchant_url)) {
                  merchantsList.push(merchant);
                  present.add(key);
                }
              } catch (_) { /* skip */ }
            }
          } catch (e) {
            console.warn('Demo overlay merge failed:', e);
          }
        }

        console.log(`📊 Total merchants: ${merchantsList.length}`);

        if (reviewsRes.ok) {
          const reviewsData = await reviewsRes.json();
          if (reviewsData.status === 'success' && reviewsData.reviews) {
            const reviewsByUrl = {};

            reviewsData.reviews.forEach(review => {
              let status = review.current_status || review.status || 'pending';
              if (status === 'approve') status = 'approved';
              if (status === 'reject') status = 'rejected';

              const normalizedUrl = normalizeMerchantUrl(review.merchant_url);
              reviewsByUrl[normalizedUrl] = status;
            });

            console.log(`📋 Reviews found: ${reviewsData.reviews.length}`);
            console.log(`📊 Status breakdown:`, {
              approved: reviewsData.reviews.filter(r => (r.current_status || r.status) === 'approved').length,
              rejected: reviewsData.reviews.filter(r => (r.current_status || r.status) === 'rejected').length,
              pending: reviewsData.reviews.filter(r => (r.current_status || r.status) === 'pending').length
            });

            merchantsList = merchantsList.map(merchant => ({
              ...merchant,
              review_status: reviewsByUrl[normalizeMerchantUrl(merchant.domain)] || 'pending'
            }));

            const merchantsWithReviews = merchantsList.filter(m => reviewsByUrl[normalizeMerchantUrl(m.domain)]).length;
            const merchantsWithoutReviews = merchantsList.length - merchantsWithReviews;
            console.log(`✅ Merchants with reviews: ${merchantsWithReviews}`);
            console.log(`⏳ Merchants pending review: ${merchantsWithoutReviews}`);
          }
        } else {
          merchantsList = merchantsList.map(merchant => ({
            ...merchant,
            review_status: 'pending'
          }));
          console.warn('⚠️ Failed to fetch reviews, marking all as pending');
        }

        setMerchants(merchantsList.map(sanitizeMerchant));
        if (!silent) setLoading(false);

        // Background: failed / retry queue (writes + orphan scans) — do not gate UI
        fetch(failedMerchantsUrl)
          .then(async (failedMerchantsRes) => {
            if (!failedMerchantsRes.ok) {
              console.warn('⚠️ Failed to fetch failed merchants list');
              setFailedMerchants([]);
              return;
            }
            const failedData = await failedMerchantsRes.json();
            const dedupedFailedMerchants = Object.values(
              (failedData.items || []).reduce((acc, item) => {
                const key = normalizeMerchantUrl(item.merchant_url || item.domain || item.merchant_name);
                if (!key) {
                  return acc;
                }

                const existing = acc[key];
                const existingTime = existing?.latest_failed_at ? new Date(existing.latest_failed_at).getTime() : 0;
                const nextTime = item?.latest_failed_at ? new Date(item.latest_failed_at).getTime() : 0;
                if (!existing || nextTime >= existingTime) {
                  acc[key] = {
                    ...item,
                    merchant_name: item.merchant_name || item.merchant_url || 'Unknown Merchant',
                    merchant_url: item.merchant_url,
                  };
                }
                return acc;
              }, {})
            );
            setFailedMerchants(dedupedFailedMerchants);
          })
          .catch((err) => {
            console.warn('⚠️ Failed merchants fetch error:', err);
            setFailedMerchants([]);
          });
      } else {
        setError('Failed to fetch merchants');
      }
    } catch (err) {
      setError('Network error while fetching merchants');
      console.error('Error fetching merchants:', err);
    } finally {
      setLoading(false);
    }
  };

  // Backup Crawl: trigger crawl for all currently-failed merchants
  const handleBackupCrawlAll = async (failedList) => {
    if (!failedList || failedList.length === 0) return;
    const urls = failedList.map((m) => m.merchant_url).filter(Boolean);
    if (urls.length === 0) return;

    try {
      setBackupCrawling(true);
      setBackupCrawlStatus(null);
      const res = await fetch(`${API_BASE}/api/merchants/backup-crawl`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urls }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Backup crawl request failed');
      setBackupCrawlBatchId(data.batch_id);
      setBackupCrawlStatus({ status: 'processing', message: `Backup crawl started for ${urls.length} merchant(s)...` });
      // Start polling for status
      pollBackupCrawlStatus(data.batch_id);
    } catch (err) {
      console.error('Backup crawl error:', err);
      setBackupCrawlStatus({ status: 'error', message: err.message });
      setBackupCrawling(false);
    }
  };

  const pollBackupCrawlStatus = (batchId) => {
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/merchants/backup-crawl-status?batch_id=${batchId}`);
        const data = await res.json();
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(interval);
          setBackupCrawling(false);
          setBackupCrawlStatus({
            status: data.status === 'completed' ? 'completed' : 'failed',
            message: data.status === 'completed'
              ? `Backup crawl complete — ${data.success_count || 0} classified, ${data.failed_count || 0} still failed`
              : `Backup crawl finished with errors`,
            ...data,
          });
          // Refresh merchant lists
          fetchMerchants();
        } else {
          setBackupCrawlStatus({
            status: 'processing',
            message: `Processing... ${(data.success_count || 0) + (data.failed_count || 0)}/${data.total_urls || '?'} done`,
            ...data,
          });
        }
      } catch {
        // Polling error — keep trying
      }
    }, 3000);
    // Cleanup after 5 minutes max
    setTimeout(() => {
      clearInterval(interval);
      setBackupCrawling(false);
    }, 300000);
  };

  const fetchMerchantDetails = async (merchantId) => {
    try {
      setDetailsLoading(true);
      setError(null);
      const tenantScope = getTenantScopeParam();
      const detailsUrl = tenantScope
        ? `${API_BASE}/api/merchants/${encodeURIComponent(merchantId)}${tenantScope}`
        : `${API_BASE}/api/merchants/${encodeURIComponent(merchantId)}`;
      let response = await fetch(detailsUrl);
      if (!response.ok && tenantScope && isDemoWorkspace()) {
        const retry = await fetch(
          `${API_BASE}/api/merchants/${encodeURIComponent(demoReportURL(merchantId))}`
        );
        if (retry.ok) response = retry;
      }
      if (response.ok) {
        const data = await response.json();

        // Fetch review data for this merchant
        try {
          const reviewQuery = new URLSearchParams({ merchant_url: merchantId });
          const viewingTenantId = localStorage.getItem('viewing_tenant_id');
          if (viewingTenantId) reviewQuery.append('tenant_id', viewingTenantId);
          const reviewResponse = await fetch(`${API_BASE}/api/v1/workflow/reviews/?${reviewQuery.toString()}`);
          if (reviewResponse.ok) {
            const reviewData = await reviewResponse.json();
            data.reviews = reviewData.reviews || [];
          }
        } catch (reviewErr) {
          console.warn('Could not fetch reviews:', reviewErr);
          data.reviews = [];
        }

        // Stored website authenticity (written automatically after MCC classification completes)
        let authSnapshot = null;
        try {
          const aq = new URLSearchParams({ merchant_url: merchantId });
          const viewingTenantId = localStorage.getItem('viewing_tenant_id');
          if (viewingTenantId) aq.append('tenant_id', viewingTenantId);
          const authRes = await fetch(`/api/v1/authenticity/result/?${aq.toString()}`);
          if (authRes.ok) {
            authSnapshot = await authRes.json();
            data.authenticity = authSnapshot;
          }
        } catch (authErr) {
          console.warn('Could not load stored authenticity:', authErr);
        }

        setSelectedMerchant(sanitizeMerchant(data));
        setShowAllProducts(false);
        setShowMetaForProduct({});
        setActiveTab("overview");
        setAuthenticityLoading(false);
        setAuthenticityPdfLoading(false);
        // Reset scroll: opening a tall detail view while the list was scrolled
        // otherwise leaves the viewport on bottom sections (e.g. Workflow review).
        window.scrollTo({ top: 0, behavior: "auto" });
        if (authSnapshot?.result) {
          setAuthenticityResult(authSnapshot.result);
          setAuthenticityError(
            authSnapshot.status === 'failed' ? authSnapshot.error_message || 'Assessment failed' : null
          );
        } else {
          setAuthenticityResult(null);
          setAuthenticityError(
            authSnapshot?.status === 'failed' ? authSnapshot.error_message || 'Assessment failed' : null
          );
        }

        // Backfill server IP location for existing merchants missing a stored row
        const loc = data.url_location || {};
        const hasLoc = loc.display || loc.ip_address || loc.country_code;
        if (!hasLoc) {
          try {
            const locRes = await fetch(`${API_BASE}/api/v1/url-location/resolve/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ merchant_url: merchantId, force: false }),
            });
            if (locRes.ok) {
              const locData = await locRes.json();
              if (locData.url_location) {
                setSelectedMerchant((prev) =>
                  prev ? { ...prev, url_location: locData.url_location } : prev
                );
              }
            }
          } catch (locErr) {
            console.warn('URL location backfill failed:', locErr);
          }
        }
      } else {
        setError('Failed to fetch merchant details');
      }
    } catch (err) {
      setError('Network error while fetching merchant details');
      console.error('Error fetching merchant details:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const deleteMerchantData = async (merchantUrl) => {
    if (!window.confirm(`Are you sure you want to delete ALL data for ${merchantUrl}?\n\nThis will remove:\n- About Us data\n- Discovered pages\n- Products/Services\n- Combined data\n- MCC classification results\n- All related information\n\nThis action cannot be undone!`)) {
      return;
    }

    setDeleting(true);
    try {
      const response = await fetch(`${API_BASE}/api/merchants/${encodeURIComponent(merchantUrl)}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        showSnackbar('Merchant data deleted successfully!', 'success');
        closeMerchantView();
        await fetchMerchants({ silent: true });
      } else {
        const error = await response.json();
        showSnackbar(`Failed to delete merchant: ${error.message || 'Unknown error'}`, 'error');
      }
    } catch (err) {
      console.error('Error deleting merchant:', err);
      showSnackbar('Network error while deleting merchant data', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const archiveMerchantData = async (merchantUrl) => {
    const targetUrl = (merchantUrl || '').trim();
    if (!targetUrl) {
      showSnackbar('No merchant URL to delete', 'error');
      return;
    }
    if (!window.confirm(`Are you sure you want to PERMANENTLY DELETE all data for ${targetUrl}?\n\nThis will delete:\n- About Us data\n- Discovered pages\n- Products/Services\n- Combined data\n- MCC classification results\n- All related information\n\nThis action CANNOT be undone.`)) {
      return;
    }

    setArchiving(true);
    try {
      const response = await fetch(`${API_BASE}/api/merchants/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ merchant_url: targetUrl }),
      });

      if (response.ok) {
        showSnackbar('Merchant deleted successfully!', 'success');
        closeMerchantView();
        await fetchMerchants({ silent: true });
      } else {
        let errorMsg = 'Unknown error';
        const rawText = await response.text();
        try {
          const errorData = JSON.parse(rawText);
          errorMsg = errorData.error || errorData.message || rawText;
        } catch {
          errorMsg = rawText.includes('<!DOCTYPE') || rawText.includes('<html')
            ? `Server error (${response.status}). Please try again or check admin_backend logs.`
            : rawText.slice(0, 300);
        }
        showSnackbar(`Failed to delete merchant: ${errorMsg}`, 'error');
      }
    } catch (err) {
      console.error('Error deleting merchant:', err);
      showSnackbar('Network error while deleting merchant data', 'error');
    } finally {
      setArchiving(false);
    }
  };

  const generateAndDownloadReport = async (merchantUrl, format) => {
    const reportKey = `${merchantUrl}-${format}`;
    setGeneratingReports(prev => new Set([...prev, reportKey]));
    showSnackbar(`Generating ${format.toUpperCase()} report for ${merchantUrl.replace(/^https?:\/\//, '')}...`, 'info');

    try {
      const { filename, blob } = await fetchGeneratedReport(merchantUrl, format);
      triggerBrowserDownload(blob, filename);
      showSnackbar(`✅ ${format.toUpperCase()} report downloaded!`, 'success');
    } catch (error) {
      console.error('Error generating report:', error);
      showSnackbar(`Failed to generate ${format.toUpperCase()} report: ${error.message}`, 'error');
    } finally {
      setGeneratingReports(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportKey);
        return newSet;
      });
    }
  };

  const handleMapToDemo = async () => {
    const ids = selectAllPages
      ? filteredMerchants.map((m) => m.id || m.merchant_url)
      : selectedMerchantsForBulk;
    if (!ids.length) {
      showSnackbar('Select at least one merchant report.', 'warning');
      return;
    }
    if (!window.confirm(`Show ${ids.length} selected report(s) in Paylegit Demo?\n\nThis maps the reports (no data copy).`)) {
      return;
    }
    try {
      setMappingToDemo(true);
      const res = await demoAPI.mapReports(ids, 'paylegit-demo');
      showSnackbar(
        `Mapped ${res.mapped} report(s) to Paylegit Demo${res.skipped ? ` (${res.skipped} skipped)` : ''}.`,
        'success',
      );
      setSelectedMerchantsForBulk([]);
    } catch (e) {
      console.error(e);
      showSnackbar(e?.message || 'Failed to map reports to Demo', 'error');
    } finally {
      setMappingToDemo(false);
    }
  };

  const handleHideFromDemo = async () => {
    const ids = selectAllPages
      ? filteredMerchants.map((m) => m.id || m.merchant_url)
      : selectedMerchantsForBulk;
    if (!ids.length) {
      showSnackbar('Select at least one merchant report.', 'warning');
      return;
    }
    if (!window.confirm(`Hide ${ids.length} report(s) from Paylegit Demo frontend?`)) {
      return;
    }
    try {
      setHidingFromDemo(true);
      const res = await demoAPI.hideReports(ids);
      showSnackbar(`Hidden ${res.hidden} report(s) from Demo.`, 'success');
      setSelectedMerchantsForBulk([]);
      await fetchMerchants({ silent: true });
    } catch (e) {
      console.error(e);
      showSnackbar(e?.message || 'Failed to hide reports from Demo', 'error');
    } finally {
      setHidingFromDemo(false);
    }
  };

  const generateBulkReports = async (format) => {
    if (selectedMerchantsForBulk.length === 0 && !selectAllPages) return;

    setBulkGenerating(true);
    try {
      let merchantsToUse = [];
      
      if (selectAllPages) {
        // Apply the same date + search filter that the table uses
        merchantsToUse = merchants.filter(m => {
          if (!merchantInDateBounds(m)) return false;
          return merchantMatchesSearch(m, searchTerm);
        });
      } else {
        // Use selected merchants from current page
        merchantsToUse = selectedMerchantsForBulk.map(id => 
          merchants.find(m => m.id === id)
        ).filter(Boolean);
      }

      if (merchantsToUse.length === 0) {
        showSnackbar('No valid merchants found', 'warning');
        return;
      }

      const urls = merchantsToUse
        .map((m) => demoReportURL(m.id || m.merchant_url || m.domain))
        .filter(Boolean);
      if (urls.length === 0) {
        showSnackbar('No valid merchants found', 'warning');
        return;
      }

      showSnackbar(`Preparing ${urls.length} ${format.toUpperCase()} report(s) the same way as a single download...`, 'info');
      const files = [];
      const failed = [];
      const pending = urls.slice();
      let finished = 0;
      const usedNames = new Set();
      const workerCount = Math.min(4, pending.length);
      const workers = Array.from({ length: workerCount }, async () => {
        while (pending.length) {
          const merchantUrl = pending.shift();
          try {
            const { filename, blob } = await fetchGeneratedReport(merchantUrl, format);
            let name = String(filename || 'report').replace(/[\\/]/g, '_');
            if (usedNames.has(name)) {
              const dot = name.lastIndexOf('.');
              const stem = dot > 0 ? name.slice(0, dot) : name;
              const ext = dot > 0 ? name.slice(dot) : '';
              name = `${stem}-${usedNames.size}${ext}`;
            }
            usedNames.add(name);
            files.push({ name, data: new Uint8Array(await blob.arrayBuffer()) });
          } catch (err) {
            console.error('Bulk item failed', merchantUrl, err);
            failed.push(merchantUrl);
          } finally {
            finished += 1;
            showSnackbar(`Prepared ${finished}/${urls.length} ${format.toUpperCase()} report(s)...`, 'info');
          }
        }
      });
      await Promise.all(workers);
      if (!files.length) {
        throw new Error('Could not generate any of the selected reports');
      }
      const zipName = `bulk_merchant_reports_${files.length}_${format}_${new Date().toISOString().split('T')[0]}.zip`;
      triggerBrowserDownload(buildStoredZip(files), zipName);
      if (failed.length) {
        showSnackbar(`Downloaded ${files.length} ${format.toUpperCase()} report(s). ${failed.length} could not be generated.`, 'warning');
      } else {
        showSnackbar(`Downloaded ${files.length} ${format.toUpperCase()} report(s).`, 'success');
      }
    } catch (error) {
      console.error('Error generating bulk reports:', error);
      showSnackbar(`Failed to generate bulk ${format.toUpperCase()} reports: ${error.message || 'Please try again.'}`, 'error');
    } finally {
      setBulkGenerating(false);
    }
  };

  const runAuthenticityAnalysis = async (merchant) => {
    setActiveTab("authenticity");
    setAuthenticityLoading(true);
    setAuthenticityError(null);
    try {
      const body = {
        merchant_url: merchant.id || merchant.merchant_url || merchant.domain,
      };
      const tid = localStorage.getItem("viewing_tenant_id");
      if (tid) body.tenant_id = tid;
      const res = await fetch("/api/v1/authenticity/analyze/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || data.detail || `HTTP ${res.status}`);
      }
      setAuthenticityResult(data.result || null);
      setSelectedMerchant((prev) =>
        prev &&
        (prev.id === merchant.id ||
          prev.merchant_url === merchant.merchant_url ||
          prev.domain === merchant.domain)
          ? { ...prev, authenticity: { status: "completed", result: data.result } }
          : prev
      );
      showSnackbar("Authenticity report is ready", "success");
    } catch (e) {
      console.error(e);
      setAuthenticityError(e.message || "Analysis failed");
      showSnackbar(e.message || "Authenticity analysis failed", "error");
    } finally {
      setAuthenticityLoading(false);
    }
  };

  const refreshMerchantCurrency = async (merchant) => {
    const merchantUrl = merchant.id || merchant.merchant_url || merchant.domain;
    if (!merchantUrl) return;
    setCurrencyRefreshing(true);
    try {
      const res = await fetch(`${API_BASE}/api/merchants/refresh-currency`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ merchant_url: merchantUrl }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || data.detail || `HTTP ${res.status}`);
      }
      const codes = data.products_currency || [];
      setSelectedMerchant((prev) =>
        prev &&
        (prev.id === merchant.id ||
          prev.merchant_url === merchant.merchant_url ||
          prev.domain === merchant.domain)
          ? { ...prev, products_currency: codes }
          : prev
      );
      if (codes.length > 0) {
        showSnackbar(`Detected ${codes.length} currency${codes.length === 1 ? "" : "ies"}: ${codes.join(", ")}`, "success");
      } else {
        showSnackbar("No product currency signals found in stored HTML", "info");
      }
      await fetchMerchantDetails(merchantUrl);
    } catch (e) {
      console.error(e);
      showSnackbar(e.message || "Currency refresh failed", "error");
    } finally {
      setCurrencyRefreshing(false);
    }
  };

  const downloadAuthenticityPdf = async () => {
    if (!authenticityResult) return;
    setAuthenticityPdfLoading(true);
    try {
      const res = await fetch("/api/v1/authenticity/pdf/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          result: authenticityResult,
          domain: authenticityResult.domain || "merchant",
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "PDF failed");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = String(authenticityResult.domain || "merchant").replace(/[^a-z0-9._-]+/gi, "_");
      a.href = url;
      a.download = `${safe}_authenticity.pdf.gz`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showSnackbar("Report downloaded", "success");
    } catch (e) {
      showSnackbar(e.message || "PDF download failed", "error");
    } finally {
      setAuthenticityPdfLoading(false);
    }
  };

  const MerchantCard = ({ merchant }) => (
    <Card className="cursor-pointer transition-all hover:shadow-md" onClick={() => openMerchantById(merchant.id)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-base font-semibold text-primary mb-1 truncate" title={displayMerchantName(merchant)}>
              {displayMerchantName(merchant)}
            </h3>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground mb-1">
              <Globe className="w-3 h-3" />
              <span>{merchant.domain}</span>
            </div>
            <div className="flex items-center space-x-2 text-xs text-muted-foreground mb-2">
              <Calendar className="w-3 h-3" />
              <span>{new Date(merchant.last_updated).toLocaleDateString()}</span>
            </div>
            {merchant.mcc_classification && merchant.mcc_classification.primary_mcc_code ? (
              <div className="space-y-1">
                <Badge className="mb-1 bg-green-600 hover:bg-green-700 text-white text-xs">
                  New MCC {merchant.mcc_classification.primary_mcc_code}
                </Badge>
                {merchant.mcc_classification.bank_provided_mcc && (
                  <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                    <span className="font-medium">Bank/PSP MCC:</span>
                    <span>{merchant.mcc_classification.bank_provided_mcc}</span>
                    {merchant.mcc_classification.mcc_validation_status === 'match' && (
                      <Badge className="text-xs bg-green-100 text-green-800 ml-1">✓ Match</Badge>
                    )}
                    {merchant.mcc_classification.mcc_validation_status === 'mismatch' && (
                      <Badge className="text-xs bg-amber-100 text-amber-800 ml-1">⚠ Mismatch</Badge>
                    )}
                  </div>
                )}
                {classificationSourceLabel(merchant.mcc_classification.source) && (
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="text-xs">
                      {classificationSourceLabel(merchant.mcc_classification.source)}
                    </Badge>
                  </div>
                )}
                {needsMccVerification(merchant.mcc_classification) && (
                  <Badge variant="outline" className="text-xs border-amber-300 text-amber-800 bg-amber-50">
                    Manual verification required
                  </Badge>
                )}
              </div>
            ) : (
              <Badge variant="secondary" className="mb-2 text-xs">
                Not classified
              </Badge>
            )}
          </div>
          <FileText className="w-5 h-5 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );

  const MerchantDetails = ({ merchant }) => (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 h-auto gap-1">
          <TabsTrigger value="overview" className="text-[10px] sm:text-xs md:text-sm px-1 sm:px-2 py-1.5 sm:py-2">Overview</TabsTrigger>
          <TabsTrigger value="about" className="text-[10px] sm:text-xs md:text-sm px-1 sm:px-2 py-1.5 sm:py-2">About &amp; Products</TabsTrigger>
          <TabsTrigger value="products_currency" className="text-[10px] sm:text-xs md:text-sm px-1 sm:px-2 py-1.5 sm:py-2">Products Currency</TabsTrigger>
          <TabsTrigger value="mcc" className="text-[10px] sm:text-xs md:text-sm px-1 sm:px-2 py-1.5 sm:py-2">MCC Classification</TabsTrigger>
          <TabsTrigger value="mcc_validation" className="text-[10px] sm:text-xs md:text-sm px-1 sm:px-2 py-1.5 sm:py-2">MCC Validation</TabsTrigger>
          <TabsTrigger value="screenshots" className="text-[10px] sm:text-xs md:text-sm px-1 sm:px-2 py-1.5 sm:py-2">TOS &amp; Policy</TabsTrigger>
          <TabsTrigger value="security" className="text-[10px] sm:text-xs md:text-sm px-1 sm:px-2 py-1.5 sm:py-2">URL Location &amp; Redirection</TabsTrigger>
          <TabsTrigger value="authenticity" className="text-[10px] sm:text-xs md:text-sm px-1 sm:px-2 py-1.5 sm:py-2">Url Authenticity</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-3 sm:space-y-4 md:space-y-6">
          {/* Compact 4-box row: Company, Contact, TOS & Policy, URL Location */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 sm:gap-3">
            {/* Company Information */}
            <Card className="min-w-0 overflow-hidden">
              <CardHeader className="py-2 px-3">
                <CardTitle className="flex items-center gap-1.5 text-xs font-semibold">
                  <Building className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                  <span className="truncate">Company Information</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5 px-3 pb-3 pt-0 text-[11px] leading-snug">
                <div className="min-w-0">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Merchant</label>
                  <p className="font-medium text-xs truncate" title={displayMerchantName(merchant)}>{displayMerchantName(merchant)}</p>
                </div>
                <div className="min-w-0">
                  <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Domain</label>
                  <a
                    href={/^https?:\/\//.test(merchant.domain) ? merchant.domain : `https://${merchant.domain}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline flex items-center gap-1 min-w-0"
                  >
                    <span className="truncate">{merchant.domain}</span>
                    <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                  </a>
                </div>
                {merchant.mcc_classification?.primary_mcc_code && (
                  <div>
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">MCC</label>
                    <div className="mt-0.5">
                      <Badge className="bg-green-600 hover:bg-green-700 text-white text-[10px] px-1.5 py-0 max-w-full truncate">
                        {merchant.mcc_classification.primary_mcc_code}
                        {merchant.mcc_classification.primary_mcc_category
                          ? ` · ${merchant.mcc_classification.primary_mcc_category}`
                          : ''}
                      </Badge>
                      {needsMccVerification(merchant.mcc_classification) && (
                        <p className="text-[10px] text-amber-800 mt-1">Manual verification required</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="min-w-0 overflow-hidden">
              <CardHeader className="py-2 px-3">
                <CardTitle className="flex items-center gap-1.5 text-xs font-semibold">
                  <Mail className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  <span className="truncate">Contact Information</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5 px-3 pb-3 pt-0 text-[11px] leading-snug">
                <div className="flex items-start gap-1.5 min-w-0">
                  <Mail className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Email</label>
                    <div className="mt-0.5 truncate">
                      {(() => {
                        const email = merchant.contact_email;
                        if (!email) return <span className="text-gray-400">Not available</span>;
                        const emailStr = String(email);
                        const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
                        const matches = emailStr.match(emailRegex) || (emailStr.includes('@') ? [emailStr] : []);
                        const valid = matches.filter((e) => !/\.(gif|jpg|png|css|js|webp|svg)/i.test(e)).slice(0, 2);
                        if (!valid.length) return <span className="text-gray-400">Not available</span>;
                        return valid.map((e, i) => (
                          <a key={i} href={`mailto:${e}`} className="text-blue-600 hover:underline block truncate" title={e}>{e}</a>
                        ));
                      })()}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-1.5 min-w-0">
                  <Phone className="w-3 h-3 text-sky-500 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Phone</label>
                    <p className="truncate">{formatContactPhoneDisplay(merchant.contact_phone)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-1.5 min-w-0">
                  <MapPin className="w-3 h-3 text-rose-500 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Address</label>
                    <p className="line-clamp-2 break-words">{formatContactAddressDisplay(merchant.contact_address)}</p>
                  </div>
                </div>
                {socialLinksForIcons(merchant.social_links).length > 0 && (
                  <div className="flex flex-wrap items-center gap-1 pt-0.5">
                    {socialLinksForIcons(merchant.social_links).map(({ key, label, url, Icon, className }) => (
                      <a
                        key={key}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={label}
                        aria-label={label}
                        className={`inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white ${className}`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </a>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* TOS & Policy */}
            <Card className="min-w-0 overflow-hidden">
              <CardHeader className="py-2 px-3">
                <CardTitle className="flex items-center gap-1.5 text-xs font-semibold">
                  <FileText className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <span className="truncate">TOS &amp; Policy</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5 px-3 pb-3 pt-0 text-[11px] leading-snug">
                {(() => {
                  const flags = getPolicyFlagEntries(merchant.policy_flags);
                  return (
                  <>
                  <div className="flex flex-wrap gap-1">
                    {flags.slice(0, 6).map(({ key, label, exists, url }) => (
                        <Badge
                          key={key}
                          className={`text-[9px] px-1.5 py-0 text-white ${exists ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                        >
                          {exists ? '✓' : '✗'} {label}
                          {exists && url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-1 inline-flex items-center"
                              title={url}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-2.5 h-2.5 inline" />
                            </a>
                          ) : null}
                        </Badge>
                    ))}
                  </div>
                  {flags.filter((f) => f.exists && f.url).slice(0, 2).map((f) => (
                    <a
                      key={`${f.key}-url`}
                      href={/^https?:\/\//i.test(f.url) ? f.url : `https://${f.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block truncate text-[10px] text-amber-800 hover:underline"
                      title={f.url}
                      onClick={(e) => {
                        e.stopPropagation();
                        const shot = (merchant.screenshots || []).find((s) =>
                          String(s.policy_type || '').toLowerCase().includes(
                            f.key === 'terms_of_service' ? 'term' : 'privacy'
                          )
                        );
                        if (shot) {
                          e.preventDefault();
                          const screenshotUrl = `${API_BASE}/api/screenshots/${shot.domain}/${shot.filename}`;
                          setSelectedScreenshot({ ...shot, url: screenshotUrl });
                          setImageZoom(1);
                          setActiveTab('screenshots');
                        }
                      }}
                    >
                      {f.label}: {f.url}
                    </a>
                  ))}
                  </>
                  );
                })()}
                {merchant.screenshots && merchant.screenshots.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5 pt-0.5">
                    {merchant.screenshots.slice(0, 3).map((screenshot, index) => {
                      const screenshotUrl = `${API_BASE}/api/screenshots/${screenshot.domain}/${screenshot.filename}`;
                      return (
                        <button
                          key={index}
                          type="button"
                          title={screenshot.policy_type || 'Screenshot'}
                          className="relative h-12 w-16 rounded border border-amber-200 overflow-hidden bg-muted hover:ring-2 hover:ring-amber-400"
                          onClick={() => {
                            setSelectedScreenshot({ ...screenshot, url: screenshotUrl });
                            setImageZoom(1);
                          }}
                        >
                          <img
                            src={screenshotUrl}
                            alt={screenshot.policy_type || `Shot ${index + 1}`}
                            className="h-full w-full object-cover"
                            onError={(e) => { e.target.style.opacity = '0.3'; }}
                          />
                          <span className="absolute inset-x-0 bottom-0 bg-black/55 text-white text-[8px] truncate px-0.5">
                            {screenshot.policy_type || 'Shot'}
                          </span>
                        </button>
                      );
                    })}
                    {merchant.screenshots.length > 3 && (
                      <button
                        type="button"
                        className="h-12 w-10 rounded border border-dashed text-[10px] text-amber-700"
                        onClick={() => setActiveTab('screenshots')}
                      >
                        +{merchant.screenshots.length - 3}
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-400 flex items-center gap-1">
                    <Image className="w-3 h-3 text-amber-500" /> No screenshots
                  </p>
                )}
                <button
                  type="button"
                  className="text-[10px] text-amber-700 hover:underline"
                  onClick={() => setActiveTab('screenshots')}
                >
                  Open TOS &amp; Policy tab →
                </button>
              </CardContent>
            </Card>

            {/* URL Location & Redirection */}
            <Card className="min-w-0 overflow-hidden">
              <CardHeader className="py-2 px-3">
                <CardTitle className="flex items-center gap-1.5 text-xs font-semibold">
                  <Globe className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                  <span className="truncate">URL Location &amp; Redirect</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5 px-3 pb-3 pt-0 text-[11px] leading-snug">
                <div className="flex items-start gap-1.5 min-w-0">
                  <MapPin className="w-3 h-3 text-indigo-500 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Location</label>
                    <p className="font-medium line-clamp-2">
                      {formatCleanUrlLocation(merchant.url_location) || 'Not available'}
                    </p>
                    {merchant.url_location?.country_code && (
                      <Badge variant="outline" className="text-[9px] mt-0.5 uppercase px-1 py-0">
                        {merchant.url_location.country_code}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-1.5">
                  <AlertTriangle className={`w-3 h-3 mt-0.5 shrink-0 ${(merchant.redirects || []).length ? 'text-amber-500' : 'text-green-500'}`} />
                  <div>
                    <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Redirects</label>
                    <p>
                      {(merchant.redirects || []).length > 0
                        ? `${merchant.redirects.length} detected`
                        : 'None detected'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="text-[10px] text-indigo-700 hover:underline"
                  onClick={() => setActiveTab('security')}
                >
                  Open location tab →
                </button>
              </CardContent>
            </Card>
          </div>

          {/* About Section - ENHANCED UI */}
          <Card className="border-l-2 sm:border-l-4 border-l-blue-500 shadow-sm">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-transparent dark:from-blue-950/20 pb-2 sm:pb-3 md:pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
                    <Building className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                    <span>About Us</span>
                  </CardTitle>
                  <CardDescription className="mt-1 flex items-center space-x-2">
                    <span className="text-xs">
                      Clean business description • Max 1250 chars
                    </span>
                  </CardDescription>
                </div>
                {merchant.about_text && !merchant.about_text.toLowerCase().includes('extraction failed') && (
                  <Badge variant="outline" className="text-xs">
                    {merchant.about_text.length} chars
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-3">
                {/* Main About Text with improved typography */}
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-line">
                    {resolveMerchantAboutText(merchant)}
                  </p>
                </div>

                {/* Warning for short descriptions */}
                {merchant.about_text && merchant.about_text.length < 60 && !merchant.about_text.toLowerCase().includes('extraction failed') && (
                  <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-950/40 border-l-4 border-amber-500 rounded-r">
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-amber-800 dark:text-amber-200">
                        <strong>Short description detected.</strong> Meta description may be used for enhanced MCC analysis.
                      </p>
                    </div>
                  </div>
                )}

                {/* Quality Metrics */}
                {merchant.about_text && !merchant.about_text.toLowerCase().includes('extraction failed') && (
                  <div className="pt-3 border-t flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center space-x-4">
                      <span>📝 {merchant.about_text.split(/\s+/).length} words</span>
                      <span>📄 {merchant.about_text.split(/\n\n/).filter(p => p.trim()).length} paragraphs</span>
                      <span>📊 {merchant.about_text.split(/[.!?]+/).filter(s => s.trim()).length} sentences</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      ✨ Junk-free
                    </Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Crawl Statistics Section */}
          {merchant.crawl_statistics && (
            <Card>
              <CardHeader className="pb-2 sm:pb-3 md:pb-4">
                <CardTitle className="flex items-center space-x-2 text-sm sm:text-base">
                  <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span>Crawl Statistics</span>
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">Pages discovered and fetched during the crawl</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 sm:space-y-4 md:space-y-6 px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                  <URLListCard 
                    count={merchant.crawl_statistics.total_discovered || 0}
                    label="Discovered"
                    color="blue"
                    urls={merchant.crawl_statistics.discovered_urls || []}
                  />
                  <URLListCard 
                    count={merchant.crawl_statistics.total_fetched || 0}
                    label="Fetched"
                    color="green"
                  />
                  <URLListCard 
                    count={merchant.crawl_statistics.product_pages_fetched || 0}
                    label="Products"
                    color="purple"
                    urls={merchant.crawl_statistics.product_urls || []}
                  />
                  <URLListCard 
                    count={merchant.crawl_statistics.about_pages_fetched || 0}
                    label="About"
                    color="orange"
                    urls={merchant.crawl_statistics.about_urls || []}
                  />
                  <URLListCard 
                    count={merchant.crawl_statistics.contact_pages_fetched || 0}
                    label="Contact"
                    color="teal"
                    urls={merchant.crawl_statistics.contact_urls || []}
                  />
                  <div className="text-center p-2 sm:p-3 md:p-4 bg-gray-50 rounded-lg">
                    <p className="text-base sm:text-lg md:text-2xl font-bold text-gray-600">{merchant.crawl_statistics.budget_allocated || 0}</p>
                    <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground">Budget</p>
                  </div>
                  <div className="text-center p-2 sm:p-3 md:p-4 bg-indigo-50 rounded-lg">
                    <p className="text-base sm:text-lg md:text-2xl font-bold text-indigo-600">
                      {merchant.crawl_statistics.product_budget_used || 0}/{merchant.crawl_statistics.budget_allocated || 0}
                    </p>
                    <p className="text-[10px] sm:text-xs md:text-sm text-muted-foreground">Product Quota</p>
                  </div>
                </div>
                
                {/* Parsing Progress section has been removed as it's available in the Processing Pipeline tab */}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Award className="w-5 h-5" />
                <span>Workflow review history</span>
              </CardTitle>
              <CardDescription>
                Status changes use the workflow control in the toolbar above. Below is the audit trail for this merchant.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {merchant.reviews && merchant.reviews.length > 0 ? (
                  <div className="space-y-4">
                    {merchant.reviews.map((review, index) => {
                      const status = review.current_status || review.status || 'pending';
                      const reviewDate = review.review_completed_at || review.updated_at || review.created_at;

                      return (
                        <div key={review.id || index} className="border rounded-lg p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <Badge
                                variant={
                                  status === 'approved'
                                    ? 'default'
                                    : status === 'rejected'
                                      ? 'destructive'
                                      : status === 'further_review'
                                        ? 'warning'
                                        : 'secondary'
                                }
                              >
                                {status.replace('_', ' ').toUpperCase()}
                              </Badge>
                              {review.workflow_name && (
                                <span className="text-xs text-muted-foreground">Workflow: {review.workflow_name}</span>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {reviewDate
                                ? new Date(reviewDate).toLocaleString('en-US', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : 'Date unknown'}
                            </span>
                          </div>

                          {(review.review_started_at || review.review_completed_at) && (
                            <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                              {review.review_started_at && (
                                <span>Started: {new Date(review.review_started_at).toLocaleDateString()}</span>
                              )}
                              {review.review_completed_at && (
                                <span>Completed: {new Date(review.review_completed_at).toLocaleDateString()}</span>
                              )}
                            </div>
                          )}

                          {review.comments && (
                            <div className="bg-muted/50 p-3 rounded">
                              <p className="text-sm font-semibold mb-1">Comments:</p>
                              <p className="text-sm">{review.comments}</p>
                            </div>
                          )}

                          {review.approval_feedback && (
                            <div className="bg-blue-50 p-3 rounded border border-blue-200">
                              <p className="text-sm font-semibold text-blue-900 mb-1">Approval feedback:</p>
                              <p className="text-sm text-blue-800">{review.approval_feedback}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Info className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">No workflow reviews yet</p>
                    <p className="text-sm text-muted-foreground mt-1">This merchant has no recorded workflow review entries.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>





        <TabsContent value="mcc" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Building className="w-5 h-5" />
                <span>MCC Classification Results</span>
                {merchant.mcc_classification && (merchant.mcc_classification.primary_mcc_code || merchant.mcc_classification.secondary_mcc_code) && (
                  <div className="flex items-center space-x-1 ml-2">
                    {merchant.mcc_classification.primary_mcc_code && (
                      <Badge className="bg-green-600 hover:bg-green-700 text-white text-xs px-2 py-1">
                        {merchant.mcc_classification.primary_mcc_code}
                      </Badge>
                    )}
                    {merchant.mcc_classification.secondary_mcc_code && (
                      <Badge variant="secondary" className="text-xs px-2 py-1">
                        {merchant.mcc_classification.secondary_mcc_code}
                      </Badge>
                    )}
                  </div>
                )}
              </CardTitle>
              <CardDescription>Merchant Category Code analysis with Primary, Secondary, and Tertiary classifications</CardDescription>
            </CardHeader>
            <CardContent>
              {merchant.mcc_classification ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-10 gap-3 items-start">
                  {/* Compact Classification Summary — ~70% */}
                  <div className="lg:col-span-7 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950 dark:to-purple-950 rounded-lg border border-blue-200 dark:border-blue-800 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-600" /> Classification Summary
                      </h4>
                      <div className="flex items-center gap-2">
                        {(merchant.mcc_classification.archived || merchant.mcc_classification.crawl_warning) && (
                          <Badge variant="outline" className="text-xs border-amber-300 text-amber-800 bg-amber-50">
                            {merchant.mcc_classification.archived ? 'From history' : 'Restored'}
                          </Badge>
                        )}
                        {classificationSourceLabel(merchant.mcc_classification.source) && (
                          <Badge variant="default" className="bg-purple-600 hover:bg-purple-700 text-xs">
                            {classificationSourceLabel(merchant.mcc_classification.source)}
                          </Badge>
                        )}
                        {needsMccVerification(merchant.mcc_classification) && (
                          <Badge variant="outline" className="text-xs border-amber-300 text-amber-800 bg-amber-50">
                            Manual verification required
                          </Badge>
                        )}
                      </div>
                    </div>

                    {merchant.mcc_classification.crawl_warning && (
                      <div className="mb-3 p-2 rounded border border-amber-200 bg-amber-50 text-amber-900 text-xs flex items-start gap-2">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>
                          Latest crawl attempt: {merchant.mcc_classification.crawl_warning}. Showing last successful classification below.
                        </span>
                      </div>
                    )}
                    {needsMccVerification(merchant.mcc_classification) && (
                      <div className="mb-3 p-2 rounded border border-amber-200 bg-amber-50 text-amber-900 text-xs flex items-start gap-2">
                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                        <span>
                          {String(merchant.mcc_classification.mcc_source || '').includes('backup')
                            ? 'PayLegit AI did not return an MCC. This code needs manual verification.'
                            : 'PayLegit AI provided this MCC code. The description needs manual verification.'}
                        </span>
                      </div>
                    )}
                    
                    {/* Compact Formatted Analysis Summary */}
                    <div className="text-xs text-blue-800 dark:text-blue-200 space-y-2">
                      {(() => {
                        const rawSummary = merchant.mcc_classification.analysis_summary ||
                                          merchant.mcc_classification.reasoning || '';
                        const isFailureOnly = /url was not crawlable|insufficient parsed/i.test(rawSummary) &&
                          !merchant.mcc_classification.primary_mcc_code;
                        const summaryText = isFailureOnly
                          ? rawSummary
                          : (rawSummary && !/^(url was not crawlable)$/i.test(rawSummary.trim())
                              ? cleanAboutDisplayText(rawSummary)
                              : (cleanAboutDisplayText(merchant.mcc_classification.primary_mcc_reasoning ||
                                 merchant.mcc_classification.reasoning ||
                                 '') || 'MCC classification completed.'));
                        const lines = summaryText.split('\n');
                        
                        // Find the index where "PRODUCTS & SERVICES" section starts
                        const productsIndex = lines.findIndex(line => line.includes('PRODUCTS & SERVICES'));
                        
                        // Show only lines up to Business Type (before PRODUCTS section)
                        const visibleLines = expandedSummary ? lines : (productsIndex > 0 ? lines.slice(0, productsIndex) : lines.slice(0, 10));
                        
                        return (
                          <>
                            {visibleLines.map((line, idx) => {
                              // Check if line is a section header
                              if (line.trim().startsWith('===')) {
                                const sectionTitle = line.replace(/===/g, '').trim();
                                return (
                                  <h5 key={idx} className="font-semibold text-blue-900 dark:text-blue-100 mt-2 mb-1 text-xs">
                                    {sectionTitle}
                                  </h5>
                                );
                              }
                              // Check if line is a list item
                              else if (line.trim().startsWith('•')) {
                                return (
                                  <p key={idx} className="ml-3 leading-tight text-xs">
                                    {line.trim()}
                                  </p>
                                );
                              }
                              // Check if line contains a colon (key-value pair)
                              else if (line.includes(':') && !line.startsWith(' ')) {
                                const [key, ...valueParts] = line.split(':');
                                const value = valueParts.join(':').trim();
                                return (
                                  <p key={idx} className="leading-tight">
                                    <span className="font-medium text-blue-900 dark:text-blue-100">{key}:</span>
                                    {value && <span className="ml-1">{value}</span>}
                                  </p>
                                );
                              }
                              // Empty line
                              else if (line.trim() === '') {
                                return <div key={idx} className="h-1" />;
                              }
                              // Regular text
                              else {
                                return line.trim() ? (
                                  <p key={idx} className="leading-tight text-xs">
                                    {line.trim()}
                                  </p>
                                ) : null;
                              }
                            })}
                            
                            {/* Compact Expand/Collapse Button */}
                            {productsIndex > 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setExpandedSummary(!expandedSummary)}
                                className="mt-2 h-7 text-xs text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 flex items-center space-x-1 p-0"
                              >
                                {expandedSummary ? (
                                  <>
                                    <ChevronUp className="w-3 h-3" />
                                    <span>Show Less</span>
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="w-3 h-3" />
                                    <span>Show More</span>
                                  </>
                                )}
                              </Button>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    
                    {/* Compact Metadata */}
                    <div className="mt-3 pt-2 border-t border-blue-200 dark:border-blue-800 space-y-1">
                      {merchant.mcc_classification.analyzed_at && (
                        <p className="text-[10px] text-blue-600 dark:text-blue-400">
                          Analyzed on {new Date(merchant.mcc_classification.analyzed_at).toLocaleString()}
                        </p>
                      )}
                      {merchant.mcc_classification.vertical && (
                        <div className="flex items-center space-x-2">
                          <span className="text-[10px] font-medium text-blue-800 dark:text-blue-200">Vertical:</span>
                          <Badge variant="outline" className="text-[10px] py-0 px-1 h-4">
                            {merchant.mcc_classification.vertical}
                          </Badge>
                          {merchant.mcc_classification.vertical_confidence && (
                            <span className="text-[10px] text-blue-600 dark:text-blue-400">
                              {(merchant.mcc_classification.vertical_confidence * 100).toFixed(0)}%
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Primary / Secondary MCC stacked — ~30% */}
                  {(() => {
                    const hasPrimary = merchant.mcc_classification.primary_mcc_code;
                    const hasSecondary = merchant.mcc_classification.secondary_mcc_code;
                    const hasTertiary = merchant.mcc_classification.tertiary_mcc_code;
                    if (!hasPrimary && !hasSecondary && !hasTertiary) {
                      return <div className="lg:col-span-3" />;
                    }
                    return (
                      <div className="lg:col-span-3 space-y-2 min-w-0">
                        {hasPrimary && (
                          <div className="p-3 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 border-2 border-purple-300 dark:border-purple-700 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <Badge className="bg-purple-600 hover:bg-purple-700 text-[10px] py-0 px-2 h-5">Primary MCC</Badge>
                              <div className="text-xs font-bold text-purple-900 dark:text-purple-100">
                                {merchant.mcc_classification.confidence || merchant.mcc_classification.primary_mcc_confidence
                                  ? `${Math.round((merchant.mcc_classification.confidence || merchant.mcc_classification.primary_mcc_confidence) * 100)}%`
                                  : 'N/A'}
                              </div>
                            </div>
                            <h3 className="text-2xl font-bold text-purple-900 dark:text-purple-100 leading-none mb-1">
                              {merchant.mcc_classification.primary_mcc_code}
                            </h3>
                            <p className="text-[11px] font-medium text-purple-800 dark:text-purple-200 line-clamp-2">
                              {merchant.mcc_classification.primary_mcc_category || 'N/A'}
                            </p>
                            {(merchant.mcc_classification.primary_mcc_description || merchant.mcc_classification.primary_mcc_category) && (
                              <p className="text-[10px] text-purple-700 dark:text-purple-300 mt-1 leading-snug line-clamp-3">
                                {merchant.mcc_classification.primary_mcc_description
                                  || merchant.mcc_classification.primary_mcc_category}
                              </p>
                            )}
                            {merchant.mcc_classification.primary_mcc_risk_level && (
                              <Badge
                                variant={
                                  String(merchant.mcc_classification.primary_mcc_risk_level || '').toLowerCase() === 'high' ? 'destructive' :
                                  String(merchant.mcc_classification.primary_mcc_risk_level || '').toLowerCase() === 'medium' ? 'warning' :
                                  'success'
                                }
                                className="text-[10px] py-0 px-1.5 h-4 mt-1"
                              >
                                {merchant.mcc_classification.primary_mcc_risk_level}
                              </Badge>
                            )}
                            {merchant.mcc_classification.primary_mcc_reasoning && (
                              <details className="text-[10px] text-purple-800 dark:text-purple-200 mt-1">
                                <summary className="cursor-pointer font-medium">View Reasoning</summary>
                                <p className="mt-1 leading-snug bg-purple-100/50 dark:bg-purple-900/50 p-2 rounded">
                                  {merchant.mcc_classification.primary_mcc_reasoning}
                                </p>
                              </details>
                            )}
                          </div>
                        )}
                        {hasSecondary && (
                          <div className="p-3 bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-950 dark:to-teal-900 border-2 border-teal-300 dark:border-teal-700 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <Badge className="bg-teal-600 text-white hover:bg-teal-700 text-[10px] py-0 px-2 h-5">Secondary MCC</Badge>
                              <div className="text-xs font-bold text-teal-900 dark:text-teal-100">
                                {merchant.mcc_classification.secondary_mcc_confidence
                                  ? `${Math.round(merchant.mcc_classification.secondary_mcc_confidence * 100)}%`
                                  : 'N/A'}
                              </div>
                            </div>
                            <h3 className="text-2xl font-bold text-teal-900 dark:text-teal-100 leading-none mb-1">
                              {merchant.mcc_classification.secondary_mcc_code}
                            </h3>
                            <p className="text-[11px] font-medium text-teal-800 dark:text-teal-200 line-clamp-2">
                              {merchant.mcc_classification.secondary_mcc_category || 'N/A'}
                            </p>
                            {(merchant.mcc_classification.secondary_mcc_description || merchant.mcc_classification.secondary_mcc_category) && (
                              <p className="text-[10px] text-teal-700 dark:text-teal-300 mt-1 leading-snug line-clamp-3">
                                {merchant.mcc_classification.secondary_mcc_description
                                  || merchant.mcc_classification.secondary_mcc_category}
                              </p>
                            )}
                            {merchant.mcc_classification.secondary_mcc_risk_level && (
                              <Badge
                                variant={
                                  String(merchant.mcc_classification.secondary_mcc_risk_level || '').toLowerCase() === 'high' ? 'destructive' :
                                  String(merchant.mcc_classification.secondary_mcc_risk_level || '').toLowerCase() === 'medium' ? 'warning' :
                                  'success'
                                }
                                className="text-[10px] py-0 px-1.5 h-4 mt-1"
                              >
                                {merchant.mcc_classification.secondary_mcc_risk_level}
                              </Badge>
                            )}
                            {merchant.mcc_classification.secondary_mcc_reasoning && (
                              <details className="text-[10px] text-teal-800 dark:text-teal-200 mt-1">
                                <summary className="cursor-pointer font-medium">View Reasoning</summary>
                                <p className="mt-1 leading-snug bg-teal-100/50 dark:bg-teal-900/50 p-2 rounded">
                                  {merchant.mcc_classification.secondary_mcc_reasoning}
                                </p>
                              </details>
                            )}
                          </div>
                        )}
                        {hasTertiary && (
                          <div className="p-3 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950 dark:to-amber-900 border-2 border-amber-300 dark:border-amber-700 rounded-lg">
                            <div className="flex items-center justify-between mb-1">
                              <Badge className="bg-amber-600 text-white hover:bg-amber-700 text-[10px] py-0 px-2 h-5">Tertiary MCC</Badge>
                              <div className="text-xs font-bold text-amber-900 dark:text-amber-100">
                                {merchant.mcc_classification.tertiary_mcc_confidence
                                  ? `${Math.round(merchant.mcc_classification.tertiary_mcc_confidence * 100)}%`
                                  : 'N/A'}
                              </div>
                            </div>
                            <h3 className="text-2xl font-bold text-amber-900 dark:text-amber-100 leading-none mb-1">
                              {merchant.mcc_classification.tertiary_mcc_code}
                            </h3>
                            <p className="text-[11px] font-medium text-amber-800 dark:text-amber-200 line-clamp-2">
                              {merchant.mcc_classification.tertiary_mcc_category || 'N/A'}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  </div>

                  {/* Compact Processing Timeline */}
                  {(merchant.mcc_classification.stage1_time_ms > 0 || 
                    merchant.mcc_classification.stage2_time_ms > 0 || 
                    merchant.mcc_classification.stage3_time_ms > 0 ||
                    merchant.mcc_classification.fetch_time_ms > 0 ||
                    merchant.mcc_classification.parse_time_ms > 0 ||
                    merchant.mcc_classification.processing_time_ms > 0) && (
                    <div className="p-3 bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-950 dark:to-teal-950 rounded-lg border border-green-200 dark:border-green-800">
                      <div className="flex items-center space-x-2 mb-2">
                        <Clock className="w-4 h-4 text-green-600" />
                        <h4 className="text-sm font-semibold text-green-900 dark:text-green-100">⏱️ Processing Timeline</h4>
                      </div>
                      
                      {/* Show compact note if fetch/parse times are not available */}
                      {(merchant.mcc_classification.fetch_time_ms === 0 && merchant.mcc_classification.parse_time_ms === 0) && (
                        <div className="mb-2 p-1.5 bg-yellow-50 dark:bg-yellow-900 rounded border border-yellow-200 dark:border-yellow-700">
                          <p className="text-[10px] text-yellow-800 dark:text-yellow-200">
                            ℹ️ Fetch and Parse timing data not available. Only MCC classification times shown.
                          </p>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                        {merchant.mcc_classification.fetch_time_ms > 0 && (
                          <div className="bg-white dark:bg-gray-800 p-2 rounded shadow-sm">
                            <p className="text-[10px] text-gray-600 dark:text-gray-400 mb-0.5">Fetch</p>
                            <p className="text-sm font-bold text-blue-600">
                              {(merchant.mcc_classification.fetch_time_ms / 1000).toFixed(1)}s
                            </p>
                          </div>
                        )}
                        
                        {merchant.mcc_classification.parse_time_ms > 0 && (
                          <div className="bg-white dark:bg-gray-800 p-2 rounded shadow-sm">
                            <p className="text-[10px] text-gray-600 dark:text-gray-400 mb-0.5">Parse</p>
                            <p className="text-sm font-bold text-indigo-600">
                              {(merchant.mcc_classification.parse_time_ms / 1000).toFixed(1)}s
                            </p>
                          </div>
                        )}
                        
                        {merchant.mcc_classification.stage1_time_ms > 0 && (
                          <div className="bg-white dark:bg-gray-800 p-2 rounded shadow-sm">
                            <p className="text-[10px] text-gray-600 dark:text-gray-400 mb-0.5">Stage 1</p>
                            <p className="text-sm font-bold text-purple-600">
                              {(merchant.mcc_classification.stage1_time_ms / 1000).toFixed(1)}s
                            </p>
                          </div>
                        )}
                        
                        {merchant.mcc_classification.stage2_time_ms > 0 && (
                          <div className="bg-white dark:bg-gray-800 p-2 rounded shadow-sm">
                            <p className="text-[10px] text-gray-600 dark:text-gray-400 mb-0.5">Stage 2</p>
                            <p className="text-sm font-bold text-orange-600">
                              {(merchant.mcc_classification.stage2_time_ms / 1000).toFixed(1)}s
                            </p>
                          </div>
                        )}
                        
                        {merchant.mcc_classification.stage3_time_ms > 0 && (
                          <div className="bg-white dark:bg-gray-800 p-2 rounded shadow-sm">
                            <p className="text-[10px] text-gray-600 dark:text-gray-400 mb-0.5">Stage 3</p>
                            <p className="text-sm font-bold text-pink-600">
                              {(merchant.mcc_classification.stage3_time_ms / 1000).toFixed(1)}s
                            </p>
                          </div>
                        )}
                        
                        {merchant.mcc_classification.processing_time_ms > 0 && (
                          <div className="bg-gradient-to-br from-green-100 to-teal-100 dark:from-green-900 dark:to-teal-900 p-2 rounded shadow-md border-2 border-green-300 dark:border-green-700">
                            <p className="text-[10px] font-semibold text-green-800 dark:text-green-200 mb-0.5">Total</p>
                            <p className="text-sm font-bold text-green-700 dark:text-green-300">
                              {(merchant.mcc_classification.processing_time_ms / 1000).toFixed(1)}s
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* Compact Visual Timeline Bar */}
                      {(merchant.mcc_classification.stage1_time_ms > 0 ||
                        merchant.mcc_classification.stage2_time_ms > 0 ||
                        merchant.mcc_classification.stage3_time_ms > 0) && (
                        <div className="mt-3 pt-2 border-t border-green-200 dark:border-green-800">
                          <p className="text-[10px] text-gray-600 dark:text-gray-400 mb-1.5">MCC Processing Breakdown</p>
                          <div className="flex w-full h-6 rounded overflow-hidden shadow-sm">
                            {merchant.mcc_classification.stage1_time_ms > 0 && (
                              <div 
                                className="bg-purple-500 flex items-center justify-center text-white text-[10px] font-medium"
                                style={{
                                  width: `${(merchant.mcc_classification.stage1_time_ms / 
                                    (merchant.mcc_classification.stage1_time_ms + 
                                     merchant.mcc_classification.stage2_time_ms + 
                                     merchant.mcc_classification.stage3_time_ms)) * 100}%`
                                }}
                              >
                                S1
                              </div>
                            )}
                            {merchant.mcc_classification.stage2_time_ms > 0 && (
                              <div 
                                className="bg-orange-500 flex items-center justify-center text-white text-[10px] font-medium"
                                style={{
                                  width: `${(merchant.mcc_classification.stage2_time_ms / 
                                    (merchant.mcc_classification.stage1_time_ms + 
                                     merchant.mcc_classification.stage2_time_ms + 
                                     merchant.mcc_classification.stage3_time_ms)) * 100}%`
                                }}
                              >
                                S2
                              </div>
                            )}
                            {merchant.mcc_classification.stage3_time_ms > 0 && (
                              <div 
                                className="bg-pink-500 flex items-center justify-center text-white text-[10px] font-medium"
                                style={{
                                  width: `${(merchant.mcc_classification.stage3_time_ms / 
                                    (merchant.mcc_classification.stage1_time_ms + 
                                     merchant.mcc_classification.stage2_time_ms + 
                                     merchant.mcc_classification.stage3_time_ms)) * 100}%`
                                }}
                              >
                                S3
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* MCC Combined Data Section */}
                  <Card className="border-2 border-blue-200 dark:border-blue-800">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2 text-blue-900 dark:text-blue-100">
                        <FileText className="w-5 h-5" />
                        <span>MCC Combined Data</span>
                      </CardTitle>
                      <CardDescription>
                        Primary MCC classification result and detailed input data from products_services_combined
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* MCC Results - Always Visible */}
                      {/* <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border-2 border-blue-300 dark:border-blue-700">
                        <div className="flex items-center space-x-2 mb-3">
                          <Award className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          <label className="text-base font-bold text-blue-900 dark:text-blue-100">
                            MCC Classification Result
                          </label>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Primary MCC:</span>
                            <Badge className="bg-blue-600 text-white text-sm px-3 py-1">
                              {merchant.mcc_classification.primary_mcc_code} - {merchant.mcc_classification.primary_mcc_name}
                            </Badge>
                            <span className="text-sm text-gray-600 dark:text-gray-400">
                              ({(merchant.mcc_classification.primary_mcc_confidence * 100).toFixed(1)}% confidence)
                            </span>
                          </div>
                          {merchant.mcc_classification.alternative_mccs && merchant.mcc_classification.alternative_mccs.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 block mb-2">Alternative MCCs:</span>
                              <div className="flex flex-wrap gap-2">
                                {merchant.mcc_classification.alternative_mccs.slice(0, 3).map((alt, idx) => (
                                  <Badge key={idx} variant="secondary" className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                                    {alt.mcc_code} - {alt.mcc_name}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div> */}

                      {/* Toggle Button for Offerings JSON */}
                      <div className="border-t pt-4">
                        <Button 
                          variant="outline" 
                          onClick={() => setShowCombinedData(!showCombinedData)}
                          className="w-full flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-800"
                        >
                          <span className="font-medium text-base">
                            {showCombinedData ? '▼ Hide' : '▶ Show'} Structured Offerings JSON (About Us, Products & Services)
                          </span>
                        </Button>
                      </div>

                      {/* Offerings JSON Display - Simplified */}
                      {showCombinedData && merchant.mcc_classification.input_data_used && (() => {
                        const data = merchant.mcc_classification.input_data_used;
                        
                        // Try to parse offerings_json
                        let offeringsJson = null;
                        try {
                          if (data.offerings_json) {
                            offeringsJson = typeof data.offerings_json === 'string' 
                              ? JSON.parse(data.offerings_json) 
                              : data.offerings_json;
                          }
                        } catch (e) {
                          console.error('Failed to parse offerings_json:', e);
                        }

                        if (!offeringsJson) {
                          return (
                            <div className="p-4 bg-yellow-50 dark:bg-yellow-950 rounded-lg border border-yellow-200 dark:border-yellow-800">
                              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                No structured offerings JSON available. This merchant may have been processed before the JSON structure update.
                              </p>
                            </div>
                          );
                        }

                        return (
                          <div className="space-y-4 mt-4">
                            {/* About Us Section */}
                            {offeringsJson.about_us && (
                              <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2 flex items-center">
                                  <span className="mr-2">📄</span>
                                  About Us ({offeringsJson.about_us.length} characters)
                                </h4>
                                <p className="text-xs text-blue-800 dark:text-blue-200 leading-relaxed whitespace-pre-wrap">
                                  {offeringsJson.about_us.substring(0, 500)}
                                  {offeringsJson.about_us.length > 500 && (
                                    <span className="block mt-2 text-blue-600 dark:text-blue-400 font-semibold">
                                      ... ({offeringsJson.about_us.length - 500} more characters)
                                    </span>
                                  )}
                                </p>
                              </div>
                            )}

                            {/* Products Section */}
                            {offeringsJson.products && offeringsJson.products.length > 0 && (
                              <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
                                <h4 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-3 flex items-center">
                                  <span className="mr-2">🛍️</span>
                                  Products ({offeringsJson.products.length} items - Max 21 with category sampling)
                                </h4>
                                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                                  {offeringsJson.products.slice(0, 12).map((product, idx) => (
                                    <div key={idx} className="p-3 bg-white dark:bg-gray-900 rounded border border-green-200 dark:border-green-800">
                                      <div className="flex items-start justify-between mb-1">
                                        <p className="text-xs font-semibold text-green-900 dark:text-green-100 flex-1">
                                          {product.name || 'Unnamed Product'}
                                        </p>
                                        {product.category && (
                                          <Badge variant="outline" className="text-xs ml-2 shrink-0">
                                            {product.category}
                                          </Badge>
                                        )}
                                      </div>
                                      {product.description && (
                                        <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                                          {product.description.substring(0, 200)}
                                          {product.description.length > 200 && '...'}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                  {offeringsJson.products.length > 12 && (
                                    <p className="text-xs text-green-700 dark:text-green-300 text-center pt-2 font-semibold">
                                      ... and {offeringsJson.products.length - 12} more products
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Services Section */}
                            {offeringsJson.services && offeringsJson.services.length > 0 && (
                              <div className="p-4 bg-purple-50 dark:bg-purple-950 rounded-lg border border-purple-200 dark:border-purple-800">
                                <h4 className="text-sm font-semibold text-purple-900 dark:text-purple-100 mb-3 flex items-center">
                                  <span className="mr-2">⚙️</span>
                                  Services ({offeringsJson.services.length} items)
                                </h4>
                                <div className="space-y-2 max-h-96 overflow-y-auto">
                                  {offeringsJson.services.slice(0, 8).map((service, idx) => (
                                    <div key={idx} className="p-3 bg-white dark:bg-gray-900 rounded border border-purple-200 dark:border-purple-800">
                                      <p className="text-xs font-semibold text-purple-900 dark:text-purple-100">
                                        {service.name || 'Unnamed Service'}
                                      </p>
                                      {service.description && (
                                        <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                                          {service.description.substring(0, 150)}
                                          {service.description.length > 150 && '...'}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                  {offeringsJson.services.length > 8 && (
                                    <p className="text-xs text-purple-700 dark:text-purple-300 text-center pt-2 font-semibold">
                                      ... and {offeringsJson.services.length - 8} more services
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Raw JSON View - Collapsible */}
                            <details className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                              <summary className="text-sm font-semibold text-gray-900 dark:text-gray-100 cursor-pointer flex items-center">
                                <span className="mr-2">📋</span>
                                Raw JSON Structure (Click to expand)
                              </summary>
                              <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-3 rounded overflow-x-auto max-h-[600px] mt-3">
                                {JSON.stringify(offeringsJson, null, 2)}
                              </pre>
                            </details>

                            {/* Data Statistics */}
                            <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Data Statistics</h4>
                              <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="text-center p-2 bg-blue-100 dark:bg-blue-900 rounded">
                                  <div className="font-bold text-blue-900 dark:text-blue-100">
                                    {offeringsJson.about_us ? offeringsJson.about_us.length : 0}
                                  </div>
                                  <div className="text-blue-700 dark:text-blue-300">About chars</div>
                                </div>
                                <div className="text-center p-2 bg-green-100 dark:bg-green-900 rounded">
                                  <div className="font-bold text-green-900 dark:text-green-100">
                                    {offeringsJson.products ? offeringsJson.products.length : 0}
                                  </div>
                                  <div className="text-green-700 dark:text-green-300">Products</div>
                                </div>
                                <div className="text-center p-2 bg-purple-100 dark:bg-purple-900 rounded">
                                  <div className="font-bold text-purple-900 dark:text-purple-100">
                                    {offeringsJson.services ? offeringsJson.services.length : 0}
                                  </div>
                                  <div className="text-purple-700 dark:text-purple-300">Services</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>

                  {/* Classification Method */}
                  {merchant.mcc_classification.classification_method && (
                    <div className="p-3 bg-muted/50 rounded-lg border">
                      <label className="text-xs font-semibold text-muted-foreground">Classification Method</label>
                      <p className="text-sm mt-1">
                        {merchant.mcc_classification.classification_method}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Building className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No MCC classification available for this merchant.</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    MCC classification will be performed during the parsing stage.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* MCC Validation Tab */}
        <TabsContent value="mcc_validation" className="space-y-6">
          {(() => {
            const mcc = merchant.mcc_classification || {};
            const bankMcc = mcc.bank_provided_mcc || '';
            const newMcc = mcc.primary_mcc_code || '';
            const status = mcc.mcc_validation_status || 'not_provided';
            const note = mcc.mcc_validation_note || '';
            const confidence = mcc.primary_mcc_confidence != null
              ? `${(mcc.primary_mcc_confidence * 100).toFixed(1)}%`
              : mcc.confidence != null
              ? `${(mcc.confidence * 100).toFixed(1)}%`
              : null;

            return (
              <div className="space-y-4">
                {/* Status banner */}
                <div className={`flex items-center gap-3 px-5 py-4 rounded-lg border ${
                  status === 'match'
                    ? 'bg-green-50 border-green-200'
                    : status === 'mismatch'
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <span className={`text-xl ${
                    status === 'match' ? 'text-green-600' :
                    status === 'mismatch' ? 'text-amber-600' : 'text-gray-400'
                  }`}>
                    {status === 'match' ? '✓' : status === 'mismatch' ? '⚠' : 'ℹ'}
                  </span>
                  <div>
                    <p className="text-sm font-semibold">
                      {status === 'match' ? 'MCC Match — Bank/PSP and AI classification agree'
                        : status === 'mismatch' ? 'MCC Mismatch — Classifications differ, review recommended'
                        : 'Bank/PSP MCC not provided'}
                    </p>
                    {note && <p className="text-xs text-muted-foreground mt-0.5">{note}</p>}
                  </div>
                  <Badge className={`ml-auto ${
                    status === 'match' ? 'bg-green-100 text-green-800' :
                    status === 'mismatch' ? 'bg-amber-100 text-amber-800' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {status === 'match' ? '✓ Match' : status === 'mismatch' ? '⚠ Mismatch' : 'Not Provided'}
                  </Badge>
                </div>
                {needsMccVerification(mcc) && (
                  <div className="flex items-center gap-3 px-5 py-4 rounded-lg border bg-amber-50 border-amber-200">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-amber-900">Manual verification required</p>
                      <p className="text-xs text-amber-800 mt-0.5">
                        {String(mcc.mcc_source || '').includes('backup')
                          ? 'PayLegit AI did not return an MCC. Confirm this code before underwriting.'
                          : 'PayLegit AI provided the MCC code. Confirm the description before underwriting.'}
                      </p>
                    </div>
                  </div>
                )}

                {/* Side-by-side comparison */}
                {(bankMcc || newMcc) && (
                  <div className="grid grid-cols-2 gap-4">
                    {/* Bank / PSP card */}
                    <Card className="border border-slate-200">
                      <CardHeader className="pb-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Bank / PSP Provided</p>
                      </CardHeader>
                      <CardContent>
                        <p className="text-xs text-muted-foreground mb-0.5">MCC Code</p>
                        <p className="font-mono text-2xl font-bold text-slate-800">{bankMcc || '—'}</p>
                      </CardContent>
                    </Card>

                    {/* AI Classified card */}
                    <Card className="border border-slate-200">
                      <CardHeader className="pb-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">AI Classified</p>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div>
                          <p className="text-xs text-muted-foreground mb-0.5">MCC Code</p>
                          <p className="font-mono text-2xl font-bold text-slate-800">{newMcc || '—'}</p>
                        </div>
                        {mcc.primary_mcc_category && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-0.5">Category</p>
                            <p className="text-sm text-slate-700">{mcc.primary_mcc_category}</p>
                          </div>
                        )}
                        <div className="flex gap-6">
                          {confidence && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-0.5">Confidence</p>
                              <p className="text-sm font-medium text-slate-700">{confidence}</p>
                            </div>
                          )}
                          {mcc.primary_mcc_risk_level && (
                            <div>
                              <p className="text-xs text-muted-foreground mb-0.5">Risk Level</p>
                              <p className="text-sm text-slate-700">{mcc.primary_mcc_risk_level}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Reasoning */}
                {(mcc.primary_mcc_reasoning || mcc.reasoning) && (
                  <Card className="border border-slate-200">
                    <CardContent className="pt-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">AI Reasoning</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">{mcc.primary_mcc_reasoning || mcc.reasoning}</p>
                    </CardContent>
                  </Card>
                )}

                {status === 'not_provided' && (
                  <p className="text-xs text-muted-foreground">
                    To enable Bank/PSP MCC validation, provide the <code>bank_provided_mcc</code> field when submitting a merchant URL.
                  </p>
                )}
              </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="screenshots" className="space-y-6">
          {/* Policy Flags Section — always show core policies green/red */}
          <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <FileText className="w-5 h-5" />
                  <span>Policy Compliance</span>
                </CardTitle>
                <CardDescription>
                  Footer-discovered policies — green if found, red if missing (Terms, Privacy, Shipping, Refund, Return)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {getPolicyFlagEntries(merchant.policy_flags).map(({ key, label, exists, url }) => (
                      <Badge 
                        key={key}
                        variant={exists ? "default" : "secondary"}
                        className={`px-3 py-1.5 ${exists ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} text-white`}
                      >
                        <span className="mr-1">{exists ? '✓' : '✗'}</span>
                        {label}
                        {exists && url && (
                          <a 
                            href={url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="ml-2 inline-flex items-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="w-3 h-3 inline" />
                          </a>
                        )}
                      </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

          {/* Screenshots Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Image className="w-5 h-5" />
                <span>Policy Screenshots</span>
              </CardTitle>
              <CardDescription>Captured screenshots of TOS / Terms of Use / Terms &amp; Conditions / Privacy pages</CardDescription>
            </CardHeader>
            <CardContent>
              {merchant.screenshots && merchant.screenshots.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {merchant.screenshots.map((screenshot, index) => {
                    // Use backend proxy to avoid CORS issues with MinIO - use API_BASE for production compatibility
                    const screenshotUrl = `${API_BASE}/api/screenshots/${screenshot.domain}/${screenshot.filename}`;
                    
                    return (
                      <div 
                        key={index}
                        className="group cursor-pointer"
                        onClick={() => {
                          setSelectedScreenshot({ ...screenshot, url: screenshotUrl });
                          setImageZoom(1);
                        }}
                      >
                        <div className="aspect-video bg-muted rounded-lg overflow-hidden border relative hover:border-blue-500 transition-all">
                          <img
                            src={screenshotUrl}
                            alt={screenshot.policy_type || `Screenshot ${index + 1}`}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform p-2"
                            onError={(e) => {
                              console.error('Failed to load screenshot:', screenshotUrl);
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIG5vdCBhdmFpbGFibGU8L3RleHQ+PC9zdmc+';
                            }}
                          />
                          {screenshot.policy_type && (
                            <div className="absolute top-2 left-2">
                              <Badge variant="secondary" className="text-xs bg-black/70 text-white">
                                {screenshot.policy_type}
                              </Badge>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                            <Image className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </div>
                        <div className="mt-2 space-y-1">
                          <p className="text-sm font-medium truncate">{screenshot.policy_type || screenshot.filename}</p>
                          <p className="text-xs text-muted-foreground truncate">{publicDisplayUrl(screenshot.page_url)}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(screenshot.timestamp).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Image className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No policy screenshots available for this merchant.</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    TOS and privacy pages are screenshotted during crawl. If a policy flag is green but no image
                    appears, the screenshot step failed and will retry on the next crawl.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          {/* URL / Server IP Location */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <MapPin className="w-5 h-5" />
                <span>URL Location</span>
              </CardTitle>
              <CardDescription>
                Actual hosting location (DNS → public IP → GeoIP), not domain registration
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                const loc = merchant.url_location || {};
                const hasLoc = loc.display || loc.ip_address || loc.country_code || loc.country_name;
                if (!hasLoc) {
                  return (
                    <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center">
                      <MapPin className="w-10 h-10 text-slate-500 mx-auto mb-3" />
                      <p className="text-slate-700 text-sm font-medium">
                        {loc.error || 'Server location not available yet.'}
                      </p>
                      <p className="text-xs text-slate-600 mt-1">
                        Location is resolved at the start of each URL scan and stored for this merchant.
                      </p>
                    </div>
                  );
                }
                const place = formatCleanUrlLocation(loc);
                const inlineParts = [place, loc.ip_address, loc.isp || loc.organization].filter(Boolean);
                const inline = inlineParts.join(' · ');
                const detailItems = [
                  { label: 'City', value: loc.city },
                  { label: 'Region', value: loc.region },
                  { label: 'Country', value: (loc.country_name || loc.country_code || '').replace(/United States of America/gi, 'United States') },
                  { label: 'IP', value: loc.ip_address },
                  { label: 'ISP', value: loc.isp || loc.organization },
                  {
                    label: 'Coords',
                    value:
                      loc.latitude != null && loc.longitude != null
                        ? `${loc.latitude}, ${loc.longitude}`
                        : '',
                  },
                  { label: 'Timezone', value: loc.timezone },
                  { label: 'Host', value: loc.host },
                ].filter((d) => d.value);
                return (
                  <div className="space-y-3">
                    {/* Single highlighted summary line */}
                    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-300 bg-slate-50 px-3 py-2.5">
                      <MapPin className="w-4 h-4 shrink-0 text-indigo-600" />
                      {loc.country_code ? (
                        <Badge variant="outline" className="text-[10px] font-bold uppercase border-indigo-300 text-indigo-800 bg-indigo-50">
                          {loc.country_code}
                        </Badge>
                      ) : null}
                      <span className="text-sm font-semibold text-slate-900 break-words">
                        {inline || 'Location resolved'}
                      </span>
                      <span className="text-[11px] font-medium text-slate-600">Server IP geo</span>
                    </div>

                    {/* All fields in one readable row */}
                    <div className="flex flex-wrap gap-x-4 gap-y-2 rounded-lg border border-slate-200 px-3 py-2.5">
                      {detailItems.map((d) => (
                        <div key={d.label} className="min-w-[7rem]">
                          <span className="block text-[10px] font-bold uppercase tracking-wide text-slate-600">
                            {d.label}
                          </span>
                          <span className={`text-sm font-semibold text-slate-900 ${d.label === 'IP' || d.label === 'Coords' ? 'font-mono text-xs' : ''}`}>
                            {d.value}
                          </span>
                        </div>
                      ))}
                    </div>

                    {loc.resolved_at && (
                      <p className="text-[11px] font-medium text-slate-600">
                        Resolved: {new Date(loc.resolved_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5" />
                <span>Security &amp; Redirect Detection</span>
              </CardTitle>
              <CardDescription>URL redirects and potential security risks</CardDescription>
            </CardHeader>
            <CardContent>
              {merchant.redirects && merchant.redirects.length > 0 ? (
                <div className="space-y-4">
                  {merchant.redirects.map((redirect, index) => (
                    <div 
                      key={index} 
                      className={`p-4 rounded-lg border-2 ${
                        redirect.risk_level === 'High' ? 'border-red-500 bg-red-50' :
                        redirect.risk_level === 'Medium' ? 'border-yellow-500 bg-yellow-50' :
                        'border-green-500 bg-green-50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <Badge variant={
                            redirect.risk_level === 'High' ? 'destructive' :
                            redirect.risk_level === 'Medium' ? 'warning' :
                            'success'
                          }>
                            {redirect.risk_level} Risk
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {redirect.redirect_count} redirect{redirect.redirect_count > 1 ? 's' : ''}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(redirect.detected_at).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="font-semibold text-muted-foreground">Original:</span>
                          <p className="mt-1 font-mono text-xs break-all">{redirect.original_url}</p>
                        </div>
                        <div className="flex items-center space-x-2 text-muted-foreground">
                          <span>↓</span>
                          <span className="text-xs">redirected to</span>
                        </div>
                        <div>
                          <span className="font-semibold text-muted-foreground">Final:</span>
                          <p className="mt-1 font-mono text-xs break-all">{redirect.final_url}</p>
                        </div>
                        
                        {redirect.redirect_chain && redirect.redirect_chain.length > 2 && (
                          <div className="mt-3 pt-3 border-t">
                            <span className="font-semibold text-muted-foreground">Redirect Chain:</span>
                            <ol className="mt-2 space-y-1 list-decimal list-inside">
                              {redirect.redirect_chain.map((url, i) => (
                                <li key={i} className="text-xs font-mono break-all text-muted-foreground">{url}</li>
                              ))}
                            </ol>
                          </div>
                        )}
                        
                        {redirect.risk_reasons && redirect.risk_reasons.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <span className="font-semibold text-muted-foreground">Risk Reasons:</span>
                            <ul className="mt-2 space-y-1 list-disc list-inside">
                              {redirect.risk_reasons.map((reason, i) => (
                                <li key={i} className="text-xs text-muted-foreground">{reason}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <AlertTriangle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                  <p className="text-muted-foreground">No redirects detected.</p>
                  <p className="text-sm text-green-600 mt-2">
                    ✓ All URLs are direct without suspicious redirects.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="about" className="space-y-6">
          {/* About Us & Products & Services - Combined Tab */}
          <Card className="border-l-4 border-l-blue-500 shadow-sm">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-transparent dark:from-blue-950/20">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <Building className="w-5 h-5 text-blue-600" />
                    <span>About Us</span>
                  </CardTitle>
                  <CardDescription className="mt-1 flex items-center space-x-2">
                    <span className="text-xs">
                      Clean business description • Max 1250 chars
                    </span>
                  </CardDescription>
                </div>
                {merchant.about_text && !merchant.about_text.toLowerCase().includes('extraction failed') && (
                  <Badge variant="outline" className="text-xs">
                    {merchant.about_text.length} chars
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-3">
                {/* Main About Text with improved typography */}
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300 whitespace-pre-line">
                    {resolveMerchantAboutText(merchant)}
                  </p>
                </div>

                {/* Warning for short descriptions */}
                {merchant.about_text && merchant.about_text.length < 60 && !merchant.about_text.toLowerCase().includes('extraction failed') && (
                  <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-950/40 border-l-4 border-amber-500 rounded-r">
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-amber-800 dark:text-amber-200">
                        <strong>Short description detected.</strong> Meta description may be used for enhanced MCC analysis.
                      </p>
                    </div>
                  </div>
                )}

                {/* Quality Metrics */}
                {merchant.about_text && !merchant.about_text.toLowerCase().includes('extraction failed') && (
                  <div className="pt-3 border-t flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center space-x-4">
                      <span>📝 {merchant.about_text.split(/\s+/).length} words</span>
                      <span>📄 {merchant.about_text.split(/\n\n/).filter(p => p.trim()).length} paragraphs</span>
                      <span>📊 {merchant.about_text.split(/[.!?]+/).filter(s => s.trim()).length} sentences</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      ✨ Junk-free
                    </Badge>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Products & Services - ENHANCED UI */}
          <Card className="border-l-4 border-l-purple-500 shadow-sm">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-transparent dark:from-purple-950/20">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <FileText className="w-5 h-5 text-purple-600" />
                    <span>Products & Services</span>
                  </CardTitle>
                  <CardDescription className="mt-1">
                    <span className="text-xs">Clean structured offerings • No pricing or junk data</span>
                  </CardDescription>
                </div>
                {merchant.products_detailed && merchant.products_detailed.length > 0 && (
                  <Badge variant="outline" className="text-xs bg-purple-50 dark:bg-purple-950">
                    {merchant.products_detailed.length} items
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {merchant.products_detailed && merchant.products_detailed.length > 0 ? (
                <div className="space-y-4">
                  <div className="grid gap-4">
                    {(showAllProducts ? merchant.products_detailed : merchant.products_detailed.slice(0, 8)).map((product, index) => (
                      <div key={index} className="group p-4 bg-gradient-to-br from-gray-50 to-gray-100/50 dark:from-gray-900 dark:to-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md hover:border-purple-300 dark:hover:border-purple-700 transition-all duration-200">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-2 flex-1">
                            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-sm font-bold text-purple-600 dark:text-purple-400">
                                {index + 1}
                              </span>
                            </div>
                            <h4 className="text-base font-semibold text-foreground group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                              {product.name}
                            </h4>
                          </div>
                          {product.category && product.category !== 'Uncategorized' && (
                            <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                              {product.category}
                            </Badge>
                          )}
                          {product.currency && (
                            <CurrencyChips raw={product.currency} className="ml-2 shrink-0 max-w-[140px]" />
                          )}
                        </div>
                        
                        {product.description && product.description !== 'No description available' ? (
                          <div className="pl-10">
                            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                              {product.description}
                            </p>
                            
                            {/* Quality Badge if description is good */}
                            {product.description.length > 50 && (
                              <div className="mt-2 flex items-center space-x-2">
                                <Badge variant="outline" className="text-xs text-green-600 dark:text-green-400 border-green-300 dark:border-green-700">
                                  ✨ Clean
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {product.description.split(/\s+/).length} words
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="pl-10 text-sm text-muted-foreground italic">No description available</p>
                        )}
                        
                        {product.meta_description && (
                          <div className="mt-3 pl-10">
                            <Button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowMetaForProduct(prev => ({
                                  ...prev,
                                  [index]: !prev[index]
                                }));
                              }}
                              variant="ghost"
                              size="sm"
                              className="text-xs h-7 px-2 hover:bg-purple-100 dark:hover:bg-purple-900"
                            >
                              {showMetaForProduct[index] ? '▼ Hide Meta' : '▶ Show Meta'}
                            </Button>
                            {showMetaForProduct[index] && (
                              <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-950/40 rounded border-l-4 border-blue-500">
                                <p className="text-xs font-semibold text-blue-900 dark:text-blue-100 mb-1">📋 Meta Description:</p>
                                <p className="text-xs text-blue-800 dark:text-blue-200 leading-relaxed">
                                  {product.meta_description}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {product.source_url && (
                          <div className="mt-3 pl-10 pt-2 border-t border-gray-200 dark:border-gray-700 flex items-center space-x-2">
                            <ExternalLink className="w-3 h-3 text-muted-foreground" />
                            <a 
                              href={publicDisplayUrl(product.source_url)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline truncate"
                            >
                              {publicDisplayUrl(product.source_url)}
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {merchant.products_detailed.length > 8 && (
                    <div className="flex justify-center pt-4">
                      <Button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowAllProducts(!showAllProducts);
                        }}
                        variant="outline"
                        className="text-sm hover:bg-purple-50 dark:hover:bg-purple-950"
                      >
                        {showAllProducts ? '▲ Show Less' : `▼ See More (${merchant.products_detailed.length - 8} more items)`}
                      </Button>
                    </div>
                  )}
                  
                  {/* Summary Stats */}
                  <div className="pt-4 border-t flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center space-x-4">
                      <span>📦 {merchant.products_detailed.length} total items</span>
                      <span>🏷️ {[...new Set(merchant.products_detailed.map(p => p.category).filter(c => c && c !== 'Uncategorized'))].length} categories</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      ✨ Price-free
                    </Badge>
                  </div>
                </div>
              ) : merchant.products && merchant.products.length > 0 ? (
                <div className="space-y-4">
                  <div className="grid gap-3">
                    {(showAllProducts ? merchant.products : merchant.products.slice(0, 8)).map((product, index) => (
                      <div key={index} className="p-3 bg-muted/50 rounded-lg border">
                        <p className="text-sm">{product}</p>
                      </div>
                    ))}
                  </div>
                  {merchant.products.length > 8 && (
                    <div className="flex justify-center pt-2">
                      <Button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowAllProducts(!showAllProducts);
                        }}
                        variant="outline"
                        className="text-sm"
                      >
                        {showAllProducts ? `▲ Show Less` : `▼ See More (${merchant.products.length - 8} more)`}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No products information available.</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Products are extracted during the crawling and parsing process.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="products_currency" className="space-y-6">
          <Card className="border-l-4 border-l-amber-500 shadow-sm">
            <CardHeader className="bg-gradient-to-r from-amber-50 to-transparent dark:from-amber-950/20">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <DollarSign className="w-5 h-5 text-amber-600" />
                    <span>Products Currency</span>
                  </CardTitle>
                  <CardDescription className="mt-1">
                    <span className="text-xs">Currencies from product HTML, price symbols, and locale/currency pages (Shopify localization, /en-gb, switcher links). Refresh re-scans stored pages and those URLs.</span>
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-amber-300 text-amber-800 hover:bg-amber-50"
                    disabled={currencyRefreshing}
                    onClick={() => refreshMerchantCurrency(merchant)}
                  >
                    {currencyRefreshing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Refreshing…
                      </>
                    ) : (
                      <>
                        <RotateCw className="w-4 h-4 mr-2" />
                        Refresh currency
                      </>
                    )}
                  </Button>
                {(() => {
                  const codes = merchant.products_currency?.length
                    ? merchant.products_currency
                    : [...new Set(
                        (merchant.products_detailed || [])
                          .flatMap((p) => String(p.currency || '').split(',').map((c) => c.trim()).filter(Boolean))
                      )];
                  return codes.length > 0 ? (
                    <Badge variant="outline" className="text-xs bg-amber-50 dark:bg-amber-950">
                      {codes.length} {codes.length === 1 ? 'currency' : 'currencies'}
                    </Badge>
                  ) : null;
                })()}
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {(() => {
                const currencyCodes = merchant.products_currency?.length
                  ? merchant.products_currency
                  : [...new Set(
                      (merchant.products_detailed || [])
                        .flatMap((p) => String(p.currency || '').split(',').map((c) => c.trim().toUpperCase()).filter(Boolean))
                    )];

                if (currencyCodes.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <DollarSign className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No product currency detected.</p>
                      <p className="text-sm text-muted-foreground mt-2 max-w-lg mx-auto">
                        Currencies are extracted when product pages include pricing signals (e.g. JSON-LD priceCurrency, og:price:currency, or visible currency symbols like ₹, $, €).
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-4 border-amber-300 text-amber-800 hover:bg-amber-50"
                        disabled={currencyRefreshing}
                        onClick={() => refreshMerchantCurrency(merchant)}
                      >
                        {currencyRefreshing ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Scanning stored HTML…
                          </>
                        ) : (
                          <>
                            <RotateCw className="w-4 h-4 mr-2" />
                            Refresh from stored crawl data
                          </>
                        )}
                      </Button>
                    </div>
                  );
                }

                return (
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-3">Merchant-level currencies</h4>
                      <CurrencyChips raw={currencyCodes.join(',')} />
                    </div>

                    {merchant.products_detailed && merchant.products_detailed.some((p) => p.currency) && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-3">Per-product currency</h4>
                        <div className="grid gap-3">
                          {merchant.products_detailed.filter((p) => p.currency).map((product, index) => (
                            <div key={index} className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                              <span className="text-sm font-medium truncate pr-4">{product.name}</span>
                              <CurrencyChips raw={product.currency} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="authenticity" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <ShieldCheck className="w-5 h-5 text-violet-600" />
                <span>Website authenticity</span>
              </CardTitle>
              <CardDescription>
                After MCC classification completes (Primary or Fallback), an authenticity assessment runs automatically using saved
                merchant data. It includes a heuristic AI-designed / synthetic site check (placeholder copy,
                stock photo CDNs, duplicated product blurbs). Re-run below to refresh after crawl data changes.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => runAuthenticityAnalysis(merchant)}
                  disabled={authenticityLoading}
                >
                  {authenticityLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Re-running…
                    </>
                  ) : (
                    <>
                      <RotateCw className="w-4 h-4 mr-2" />
                      Re-run assessment
                    </>
                  )}
                </Button>
                {authenticityResult && (
                  <Button variant="outline" size="sm" onClick={downloadAuthenticityPdf} disabled={authenticityPdfLoading}>
                    {authenticityPdfLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing file…
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" /> Download report
                      </>
                    )}
                  </Button>
                )}
              </div>

              {authenticityLoading && (
                <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground border rounded-lg bg-muted/30">
                  <Loader2 className="w-10 h-10 animate-spin text-violet-600" />
                  <p className="text-sm">Analysing saved website content for this merchant…</p>
                </div>
              )}

              {!authenticityLoading && authenticityError && (
                <div className="rounded-md border border-red-200 bg-red-50 text-red-800 p-4 text-sm">{authenticityError}</div>
              )}

              {!authenticityLoading && authenticityResult && (
                <div className="space-y-4 border rounded-lg p-4 bg-card">
                  <div className="flex flex-wrap justify-between gap-2 items-start border-b pb-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Domain</p>
                      <p className="text-lg font-semibold">{authenticityResult.domain}</p>
                      <p className="text-xs text-muted-foreground">
                        {authenticityResult.analysed_at
                          ? new Date(authenticityResult.analysed_at).toLocaleString()
                          : ""}
                      </p>
                    </div>
                    <div className="text-right">
                      <p
                        className={`text-2xl font-bold ${
                          String(
                            authenticityResult.overall_risk_level ||
                              authenticityResult.risk_display?.level ||
                              ""
                          ).toLowerCase() === "high"
                            ? "text-red-600"
                            : String(
                                  authenticityResult.overall_risk_level ||
                                    authenticityResult.risk_display?.level ||
                                    ""
                                ).toLowerCase() === "medium"
                              ? "text-amber-600"
                              : "text-emerald-600"
                        }`}
                      >
                        {authenticityResult.overall_risk_level ||
                        authenticityResult.risk_display?.level
                          ? `${authenticityResult.overall_risk_level || authenticityResult.risk_display?.level} Risk`
                          : "—"}
                      </p>
                      {authenticityResult.url_redirect_detected && (
                        <p className="text-xs text-red-600 mt-1">URL redirect detected (High risk)</p>
                      )}
                    </div>
                  </div>

                  {authenticityResult.verdict_summary && (
                    <div className="rounded-lg border border-red-200 bg-red-50/80 p-3 text-sm text-slate-800">
                      {authenticityResult.verdict_summary}
                    </div>
                  )}

                  <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-8 gap-2">
                    {[
                      ["about_us", "About us"],
                      ["products_services", "Products"],
                      ["pricing", "Pricing"],
                      ["contact", "Contact"],
                      ["tos", "TOS"],
                      ["policy", "Policy"],
                      ["ai_synthetic", "AI / fake"],
                      ["ad_signals", "Ad signals"],
                    ].map(([key, label]) => {
                      const sec = (authenticityResult.sections || {})[key] || {};
                      const lvl = (sec.risk_level || "NONE").toUpperCase();
                      const color =
                        lvl === "HIGH"
                          ? "bg-red-100 text-red-800 border-red-200"
                          : lvl === "MEDIUM"
                            ? "bg-amber-100 text-amber-900 border-amber-200"
                            : lvl === "LOW"
                              ? "bg-emerald-100 text-emerald-900 border-emerald-200"
                              : "bg-slate-100 text-slate-700 border-slate-200";
                      return (
                        <div key={key} className={`rounded-md border p-2 text-center ${color}`}>
                          <div className="text-[10px] font-medium uppercase tracking-wide">{label}</div>
                          <div className="text-xs font-bold">{lvl}</div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="space-y-3">
                    {[
                      ["about_us", "About us"],
                      ["products_services", "Products / services"],
                      ["pricing", "Pricing"],
                      ["contact", "Contact"],
                      ["tos", "Terms of service"],
                      ["policy", "Policy"],
                      ["ai_synthetic", "AI-designed / synthetic site"],
                      ["ad_signals", "Ad and social signals"],
                    ].map(([key, title]) => {
                      const sec = (authenticityResult.sections || {})[key] || {};
                      const flags = sec.flags || [];
                      const pos = sec.positive_signals || [];
                      return (
                        <div key={key} className="rounded-lg border bg-white p-3 shadow-sm">
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="font-semibold text-sm">{title}</h4>
                            <Badge variant="outline" className="text-xs">
                              {(sec.risk_level || "NONE").toUpperCase()}
                            </Badge>
                          </div>
                          <ul className="space-y-1 text-sm">
                            {flags.map((f, i) => (
                              <li key={`f-${i}`} className="flex gap-2 text-red-700">
                                <span>✗</span>
                                <span>{f}</span>
                              </li>
                            ))}
                            {pos.map((f, i) => (
                              <li key={`p-${i}`} className="flex gap-2 text-emerald-700">
                                <span>✓</span>
                                <span>{f}</span>
                              </li>
                            ))}
                          </ul>
                          {sec.notes && String(sec.notes).trim() ? (
                            <p className="text-xs text-muted-foreground mt-2 whitespace-pre-wrap border-t pt-2">
                              {sec.notes}
                            </p>
                          ) : null}
                          {key === "pricing" && sec.urgency_phrase_count != null && (
                            <p className="text-xs text-muted-foreground mt-2">
                              Promotional phrases counted: {sec.urgency_phrase_count} · unusual discount pattern:{" "}
                              {sec.discount_anomaly ? "yes" : "no"}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!authenticityLoading && !authenticityError && !authenticityResult && (
                <p className="text-sm text-muted-foreground">
                  No saved assessment yet. This is created automatically in the background when MCC classification
                  finishes. Close and reopen this merchant, or wait a short time and refresh, if classification just
                  completed.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );

  return (
    <div className="w-full space-y-5">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight shrink-0">Merchant Reports</h1>
            {selectedMerchant && reportsMainTab === 'merchants' ? (
              <>
                <span className="hidden sm:inline text-gray-300 text-lg select-none pl-1">|</span>
                <span
                  className="text-sm sm:text-base font-semibold text-gray-800 truncate max-w-[10rem] sm:max-w-[16rem] md:max-w-[22rem] pl-1 sm:pl-2"
                  title={displayMerchantName(selectedMerchant)}
                >
                  {displayMerchantName(selectedMerchant)}
                </span>
              </>
            ) : (
              reportsMainTab === 'merchants' && (
                <p className="w-full basis-full mt-0.5 text-xs sm:text-sm text-gray-500">
                  Detailed analysis and information for discovered merchants
                </p>
              )
            )}
          </div>

          {/* Report details actions — right side */}
          {selectedMerchant && reportsMainTab === 'merchants' && (
            <div className="flex items-center flex-wrap justify-end gap-1.5 sm:gap-2 shrink-0 ml-auto">
              <ReviewButton
                reportId={selectedMerchant.id}
                merchantUrl={selectedMerchant.merchant_url || selectedMerchant.domain}
                currentStatus={selectedMerchant.review_status || 'pending'}
                onReviewComplete={() => {
                  fetchMerchantDetails(selectedMerchant.id);
                  fetchMerchants();
                }}
              />
              <Button
                variant="secondary"
                size="sm"
                className={`h-8 flex items-center gap-1.5 ${ORANGE_BTN}`}
                onClick={() => setActiveTab("authenticity")}
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Authenticity report</span>
                <span className="md:hidden">Auth</span>
              </Button>
              <Button
                variant="destructive"
                size="sm"
                className="h-8"
                onClick={() => archiveMerchantData(selectedMerchant.merchant_url || selectedMerchant.domain)}
                disabled={archiving}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                <span>{archiving ? 'Deleting...' : 'Delete'}</span>
              </Button>
              <Button variant="outline" size="sm" className={`h-8 ${ORANGE_BTN}`} onClick={closeMerchantView}>
                Close
              </Button>
              <Button
                variant="outline"
                size="sm"
                className={`h-8 flex items-center gap-1 ${ORANGE_BTN}`}
                onClick={closeMerchantView}
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Back
              </Button>
            </div>
          )}

          {/* Controls: date filter + search (listing only) */}
          {reportsMainTab === 'merchants' && !selectedMerchant && !merchantParam && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0 ml-auto">
          <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
            {[
              { key: 'today',  label: 'Today'   },
              { key: '7days',  label: '7 Days'  },
              { key: '30days', label: '30 Days' },
              { key: 'all',    label: 'All'     },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => {
                  setDateFilter(key);
                  setCustomDateFrom('');
                  setCustomDateTo('');
                  setCurrentPage(1);
                  setFailedCurrentPage(1);
                }}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  dateFilter === key
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1.5 px-2 py-1 border border-gray-200 rounded-lg bg-white shadow-sm shrink-0">
            <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <input
              type="date"
              value={customDateFrom}
              onChange={(e) => {
                const v = e.target.value;
                setCustomDateFrom(v);
                setDateFilter(v || customDateTo ? 'custom' : 'all');
                setCurrentPage(1);
                setFailedCurrentPage(1);
              }}
              className="text-xs border-0 focus:outline-none focus:ring-0 text-gray-700 bg-transparent w-[118px] h-7"
              title="From date"
              aria-label="From date"
            />
            <span className="text-gray-300 text-xs">–</span>
            <input
              type="date"
              value={customDateTo}
              min={customDateFrom || undefined}
              onChange={(e) => {
                const v = e.target.value;
                setCustomDateTo(v);
                setDateFilter(customDateFrom || v ? 'custom' : 'all');
                setCurrentPage(1);
                setFailedCurrentPage(1);
              }}
              className="text-xs border-0 focus:outline-none focus:ring-0 text-gray-700 bg-transparent w-[118px] h-7"
              title="To date"
              aria-label="To date"
            />
            {(customDateFrom || customDateTo) && (
              <button
                type="button"
                onClick={() => {
                  setCustomDateFrom('');
                  setCustomDateTo('');
                  setDateFilter('all');
                  setCurrentPage(1);
                  setFailedCurrentPage(1);
                }}
                className="p-0.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                title="Clear date range"
                aria-label="Clear date range"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          <div className="flex-1 sm:max-w-md w-full">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
              <Input
                type="text"
                placeholder="Search by merchant name, domain, or URL..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                  setFailedCurrentPage(1);
                }}
                className="pl-10 border h-8 text-xs sm:h-9 sm:text-sm md:h-10"
              />
            </div>
          </div>
          </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 border-b border-gray-200 pb-0">
        <button
          type="button"
          onClick={() => { setReportsMainTab('merchants'); closeMerchantView(); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            reportsMainTab === 'merchants'
              ? ORANGE_TAB_ACTIVE
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="inline-flex items-center gap-1.5">
            <FileText className="w-4 h-4" /> Reports
          </span>
        </button>
        <button
          type="button"
          onClick={() => setReportsMainTab('history')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            reportsMainTab === 'history'
              ? ORANGE_TAB_ACTIVE
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <span className="inline-flex items-center gap-1.5">
            <History className="w-4 h-4" /> Merchant Risk History
          </span>
        </button>
      </div>

      {reportsMainTab === 'history' ? (
        <MerchantHistoryPage key={`history-${searchTerm}`} embedded initialSearch={searchTerm} />
      ) : (
      <>
      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="text-destructive">{error}</div>
          </CardContent>
        </Card>
      )}

      {selectedMerchant ? (
        <MerchantDetails merchant={selectedMerchant} />
      ) : (merchantParam || detailsLoading) ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center space-y-2">
            <Loader2 className="w-8 h-8 animate-spin text-[#F5A623] mx-auto" />
            <p className="text-sm text-muted-foreground">
              {detailsLoading ? 'Opening report…' : 'Loading merchant report…'}
            </p>
            {error && (
              <div className="space-y-2">
                <p className="text-sm text-destructive">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    openedMerchantRef.current = null;
                    fetchMerchantDetails(merchantParam);
                  }}
                >
                  Retry
                </Button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground">Loading merchants...</div>
            </div>
          ) : merchants.length === 0 && failedMerchants.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No merchants found.</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Start by running an ATS crawl to discover merchants.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (() => {
            const dateBounds = getDateFilterBounds();
            const inFailedDateBounds = (merchant) => {
              if (!dateBounds) return true;
              const failedAt = merchant.latest_failed_at ? new Date(merchant.latest_failed_at) : null;
              if (!failedAt || Number.isNaN(failedAt.getTime())) return false;
              if (dateBounds.from && failedAt < dateBounds.from) return false;
              if (dateBounds.to && failedAt > dateBounds.to) return false;
              return true;
            };

            // Filter merchants by date + search term
            const filteredMerchants = merchants.filter((merchant) => {
              if (!merchantInDateBounds(merchant)) return false;
              return merchantMatchesSearch(merchant, searchTerm);
            })
            // Newest classification first, then company name for stable ties
            .sort((a, b) => {
              const aTime = getMerchantClassifiedAt(a)?.getTime() || 0;
              const bTime = getMerchantClassifiedAt(b)?.getTime() || 0;
              if (bTime !== aTime) return bTime - aTime;
              return displayMerchantName(a).localeCompare(displayMerchantName(b));
            });

            const filteredFailedMerchants = failedMerchants.filter((merchant) => {
              if (!inFailedDateBounds(merchant)) return false;
              const failureReason = (merchant.reason_summary || merchant.failure_reason || '').toLowerCase();
              const search = searchTerm.toLowerCase();
              const qNorm = normalizeMerchantUrl(searchTerm);
              const merchantName = normalizeMerchantUrl(merchant.merchant_name || '');
              const merchantUrl = normalizeMerchantUrl(merchant.merchant_url || '');
              return merchantMatchesSearch(
                { company_name: merchant.merchant_name, domain: merchant.merchant_url, merchant_url: merchant.merchant_url },
                searchTerm,
              ) || failureReason.includes(search)
                || (qNorm && merchantName.includes(qNorm))
                || (qNorm && merchantUrl.includes(qNorm));
            }).sort((a, b) => {
              const aTime = a.latest_failed_at ? new Date(a.latest_failed_at).getTime() : 0;
              const bTime = b.latest_failed_at ? new Date(b.latest_failed_at).getTime() : 0;
              return bTime - aTime;
            });

            // Calculate pagination
            const totalPages = Math.ceil(filteredMerchants.length / itemsPerPage);
            const startIndex = (currentPage - 1) * itemsPerPage;
            const endIndex = startIndex + itemsPerPage;
            const paginatedMerchants = filteredMerchants.slice(startIndex, endIndex);

            const failedTotalPages = Math.ceil(filteredFailedMerchants.length / itemsPerPage);
            const failedStartIndex = (failedCurrentPage - 1) * itemsPerPage;
            const failedEndIndex = failedStartIndex + itemsPerPage;
            const paginatedFailedMerchants = filteredFailedMerchants.slice(failedStartIndex, failedEndIndex);

            // Reset to page 1 if current page exceeds total pages
            if (currentPage > totalPages && totalPages > 0) {
              setCurrentPage(1);
            }
            if (failedCurrentPage > failedTotalPages && failedTotalPages > 0) {
              setFailedCurrentPage(1);
            }

            const isFailedView = listView === 'failed';
            const activeItems = isFailedView ? filteredFailedMerchants : filteredMerchants;
            const activeCurrentPage = isFailedView ? failedCurrentPage : currentPage;
            const activeTotalPages = isFailedView ? failedTotalPages : totalPages;
            const activeStartIndex = isFailedView ? failedStartIndex : startIndex;
            const activeEndIndex = isFailedView ? failedEndIndex : endIndex;
            const setActivePage = (page) => {
              if (isFailedView) {
                setFailedCurrentPage(page);
              } else {
                setCurrentPage(page);
              }
            };

            return (
              <div className="space-y-4">
                {/* Backup crawl button — only shown in failed view with items */}
                {isFailedView && filteredFailedMerchants.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleBackupCrawlAll(filteredFailedMerchants)}
                      disabled={backupCrawling}
                      className="flex items-center gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                    >
                      {backupCrawling ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
                      {backupCrawling ? 'Crawling...' : `Backup Crawl All (${filteredFailedMerchants.length})`}
                    </Button>
                  </div>
                )}

                {/* Backup crawl status banner */}
                {backupCrawlStatus && isFailedView && (
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm ${
                    backupCrawlStatus.status === 'completed' ? 'bg-green-50 text-green-700 border border-green-200' :
                    backupCrawlStatus.status === 'error' || backupCrawlStatus.status === 'failed' ? 'bg-red-50 text-red-700 border border-red-200' :
                    'bg-amber-50 text-amber-700 border border-amber-200'
                  }`}>
                    {backupCrawlStatus.status === 'processing' && <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />}
                    {backupCrawlStatus.status === 'completed' && <CheckCircle className="w-4 h-4 flex-shrink-0" />}
                    {(backupCrawlStatus.status === 'error' || backupCrawlStatus.status === 'failed') && <AlertTriangle className="w-4 h-4 flex-shrink-0" />}
                    <span>{backupCrawlStatus.message}</span>
                    {backupCrawlStatus.status !== 'processing' && (
                      <button onClick={() => setBackupCrawlStatus(null)} className="ml-auto text-current opacity-60 hover:opacity-100">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}

                {/* Results count — shown when any filter is active */}
                {(searchTerm || dateFilter !== 'all') && (
                  <p className="text-sm text-muted-foreground">
                    {dateFilter !== 'all' && (
                      <span className="inline-flex items-center gap-1 mr-2">
                        <Clock className="w-3.5 h-3.5" />
                        {dateFilter === 'today' ? 'Today' :
                         dateFilter === '7days' ? 'Last 7 days' :
                         dateFilter === '30days' ? 'Last 30 days' :
                         dateFilter === 'custom'
                           ? `${customDateFrom || '…'} – ${customDateTo || '…'}`
                           : 'Filtered'}:
                      </span>
                    )}
                    Found {activeItems.length} {isFailedView ? 'failed merchant record' : 'merchant'}{activeItems.length !== 1 ? 's' : ''}
                  </p>
                )}

                {/* Top Pagination Controls */}
                {activeTotalPages > 1 && activeItems.length > 0 && (
                  <div className="flex items-center justify-end">
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2 text-xs sm:h-8 sm:px-3 sm:text-sm"
                        onClick={() => setActivePage(Math.max(1, activeCurrentPage - 1))}
                        disabled={activeCurrentPage === 1}
                      >
                        <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                        <span className="hidden sm:inline">Previous</span>
                      </Button>
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, activeTotalPages) }, (_, i) => {
                          let pageNum;
                          if (activeTotalPages <= 5) {
                            pageNum = i + 1;
                          } else if (activeCurrentPage <= 3) {
                            pageNum = i + 1;
                          } else if (activeCurrentPage >= activeTotalPages - 2) {
                            pageNum = activeTotalPages - 4 + i;
                          } else {
                            pageNum = activeCurrentPage - 2 + i;
                          }
                          return (
                            <Button
                              key={pageNum}
                              variant={activeCurrentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              className={`h-7 w-7 p-0 text-xs sm:h-8 sm:w-8 sm:text-sm ${activeCurrentPage === pageNum ? ORANGE_BTN : ''}`}
                              onClick={() => setActivePage(pageNum)}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className={`h-7 px-2 text-xs sm:h-8 sm:px-3 sm:text-sm ${ORANGE_BTN}`}
                        onClick={() => setActivePage(Math.min(activeTotalPages, activeCurrentPage + 1))}
                        disabled={activeCurrentPage === activeTotalPages}
                      >
                        <span className="hidden sm:inline">Next</span>
                        <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 sm:ml-1" />
                      </Button>
                    </div>
                  </div>
                )}

                {/* Merchants Table */}
                {activeItems.length === 0 ? (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center py-8">
                        <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">{isFailedView ? 'No failed merchants match your filters.' : 'No merchants match your search.'}</p>
                        <p className="text-sm text-muted-foreground mt-2">
                          {isFailedView ? 'Try a different search term or date filter.' : 'Try a different search term.'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ) : isFailedView ? (
                  <>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-gray-50 dark:bg-gray-900">
                                <TableHead className="text-sm font-semibold">Merchant</TableHead>
                                <TableHead className="text-sm font-semibold">Reason</TableHead>
                                <TableHead className="text-sm font-semibold">Details</TableHead>
                                <TableHead className="text-sm font-semibold text-center">Retries</TableHead>
                                <TableHead className="text-sm font-semibold text-center">Status</TableHead>
                                <TableHead className="text-sm font-semibold text-right">Last Failed</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {paginatedFailedMerchants.map((merchant) => (
                                <TableRow key={merchant.merchant_url} className="h-16 align-top">
                                  <TableCell className="py-4">
                                    <div className="space-y-1">
                                      <div className="font-medium">{merchant.merchant_name || merchant.merchant_url}</div>
                                      <div className="text-xs text-muted-foreground break-all">{merchant.merchant_url}</div>
                                    </div>
                                  </TableCell>
                                  <TableCell className="py-4">
                                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                                      {merchant.reason_summary || merchant.failure_reason || 'Unknown failure'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="py-4 text-sm text-muted-foreground max-w-md">
                                    <div className="line-clamp-3 break-words">
                                      {merchant.error_message || 'No error details recorded'}
                                    </div>
                                  </TableCell>
                                  <TableCell className="py-4 text-center">
                                    {merchant.retry_count || 0}
                                  </TableCell>
                                  <TableCell className="py-4 text-center">
                                    <Badge
                                      className={
                                        merchant.status === 'retrying' ? 'bg-amber-100 text-amber-800' :
                                        merchant.status === 'pending' ? 'bg-red-100 text-red-800' :
                                        'bg-gray-100 text-gray-800'
                                      }
                                    >
                                      {merchant.status || 'failed'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="py-4 text-right text-sm text-muted-foreground whitespace-nowrap">
                                    {merchant.latest_failed_at ? new Date(merchant.latest_failed_at).toLocaleString() : '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    </Card>

                    {failedTotalPages > 1 && (
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                          Showing {failedStartIndex + 1} to {Math.min(failedEndIndex, filteredFailedMerchants.length)} of {filteredFailedMerchants.length} failed merchants
                        </p>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFailedCurrentPage(Math.max(1, failedCurrentPage - 1))}
                            disabled={failedCurrentPage === 1}
                          >
                            <ChevronLeft className="w-4 h-4 mr-1" />
                            Previous
                          </Button>
                          <div className="flex items-center space-x-1">
                            {Array.from({ length: Math.min(5, failedTotalPages) }, (_, i) => {
                              let pageNum;
                              if (failedTotalPages <= 5) {
                                pageNum = i + 1;
                              } else if (failedCurrentPage <= 3) {
                                pageNum = i + 1;
                              } else if (failedCurrentPage >= failedTotalPages - 2) {
                                pageNum = failedTotalPages - 4 + i;
                              } else {
                                pageNum = failedCurrentPage - 2 + i;
                              }
                              return (
                                <Button
                                  key={pageNum}
                                  variant={failedCurrentPage === pageNum ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => setFailedCurrentPage(pageNum)}
                                  className={`w-10 ${failedCurrentPage === pageNum ? ORANGE_BTN : ''}`}
                                >
                                  {pageNum}
                                </Button>
                              );
                            })}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className={ORANGE_BTN}
                            onClick={() => setFailedCurrentPage(Math.min(failedTotalPages, failedCurrentPage + 1))}
                            disabled={failedCurrentPage === failedTotalPages}
                          >
                            Next
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>                    <Card>
                      <CardContent className="pt-6">
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-gray-50 dark:bg-gray-900">
                                <TableHead className="w-[4%] text-center text-sm font-semibold">#</TableHead>
                                <TableHead className="w-[4%] text-center text-sm font-semibold">
                                  <label
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-md cursor-pointer hover:bg-blue-50 transition-colors"
                                    onClick={(e) => e.stopPropagation()}
                                    title="Select all on this page"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedMerchantsForBulk.length === paginatedMerchants.length && paginatedMerchants.length > 0}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        const allMerchantIds = paginatedMerchants.map(m => m.id);
                                        setSelectedMerchantsForBulk(e.target.checked ? allMerchantIds : []);
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="h-4 w-4 rounded border-gray-300 text-blue-600 accent-blue-600 cursor-pointer transition-transform duration-150 ease-out hover:scale-110 focus:ring-2 focus:ring-blue-400 focus:ring-offset-1"
                                    />
                                  </label>
                                </TableHead>
                                <TableHead className="w-[18%] text-sm font-semibold">Merchant Name</TableHead>
                                <TableHead className="w-[14%] text-sm font-semibold">Domain</TableHead>
                                <TableHead className="w-[11%] text-sm font-semibold">Classified</TableHead>
                                <TableHead className="w-[7%] text-sm font-semibold">MCC</TableHead>
                                <TableHead className="w-[18%] text-sm font-semibold">Description</TableHead>
                                <TableHead className="w-[9%] text-sm font-semibold text-center">Validation</TableHead>
                                <TableHead className="w-[10%] text-sm font-semibold text-center">Review Status</TableHead>
                                <TableHead className="w-[26%] text-center text-sm font-semibold">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                          <TableBody>
                            {paginatedMerchants.map((merchant, rowIndex) => (
                              <TableRow 
                                key={merchant.report_key || `${merchant.id}|${merchant.classified_at || rowIndex}`} 
                                className="cursor-pointer hover:bg-muted/50 h-16"
                                onClick={() => openMerchantById(merchant.id)}
                              >
                                <TableCell className="text-center py-4 text-sm text-muted-foreground font-medium">
                                  {startIndex + rowIndex + 1}
                                </TableCell>
                                <TableCell
                                  className="text-center py-4"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <label
                                    className="inline-flex items-center justify-center w-8 h-8 rounded-md cursor-pointer hover:bg-blue-50 active:bg-blue-100 transition-colors"
                                    title="Select merchant"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedMerchantsForBulk.includes(merchant.id)}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        setSelectedMerchantsForBulk(prev =>
                                          prev.includes(merchant.id)
                                            ? prev.filter(id => id !== merchant.id)
                                            : [...prev, merchant.id]
                                        );
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="h-4 w-4 rounded border-gray-300 text-blue-600 accent-blue-600 cursor-pointer transition-transform duration-150 ease-out hover:scale-110 focus:ring-2 focus:ring-blue-400 focus:ring-offset-1"
                                    />
                                  </label>
                                </TableCell>
                                <TableCell className="font-medium text-base py-4 max-w-[220px]">
                                  <div className="flex items-center space-x-3 min-w-0">
                                    <Building className="w-6 h-6 text-muted-foreground flex-shrink-0" />
                                    <span className="truncate" title={displayMerchantName(merchant)}>
                                      {displayMerchantName(merchant)}
                                    </span>
                                  </div>
                                </TableCell>
                                <TableCell className="text-sm py-4 text-muted-foreground">{merchant.domain}</TableCell>
                                <TableCell className="text-sm py-4 text-muted-foreground whitespace-nowrap">
                                  {(() => {
                                    const classified = getMerchantClassifiedAt(merchant);
                                    if (!classified) return '—';
                                    return classified.toLocaleString(undefined, {
                                      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                                    });
                                  })()}
                                </TableCell>
                                <TableCell className="text-sm py-4">
                                  {(merchant.mcc_code || merchant.mcc_classification?.primary_mcc_code) ? (
                                    <div className="flex flex-col items-start gap-1">
                                      <Badge variant="outline" className="bg-green-600 hover:bg-green-700 text-white text-sm px-2 py-1 font-mono">
                                        {merchant.mcc_code || merchant.mcc_classification?.primary_mcc_code}
                                      </Badge>
                                      {needsMccVerification(merchant.mcc_classification) && (
                                        <span className="text-[10px] text-amber-800">Verify</span>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm py-4 text-muted-foreground max-w-[220px]">
                                  <span className="line-clamp-2" title={merchant.mcc_classification?.primary_mcc_description || merchant.mcc_classification?.primary_mcc_category || ''}>
                                    {merchant.mcc_classification?.primary_mcc_description
                                      || merchant.mcc_classification?.primary_mcc_category
                                      || '—'}
                                  </span>
                                </TableCell>
                                <TableCell className="text-center py-4">
                                  {(() => {
                                    const vs = merchant.mcc_classification?.mcc_validation_status;
                                    if (vs === 'match') return <Badge className="bg-green-100 text-green-800 text-xs">✓ Match</Badge>;
                                    if (vs === 'mismatch') return <Badge className="bg-amber-100 text-amber-800 text-xs">⚠ Mismatch</Badge>;
                                    return <span className="text-muted-foreground text-xs">—</span>;
                                  })()}
                                </TableCell>
                                <TableCell className="text-center py-4">
                                  {merchant.review_status ? (
                                    <Badge
                                      className={
                                        merchant.review_status === 'approved' ? 'bg-green-100 text-green-800' :
                                        merchant.review_status === 'rejected' ? 'bg-red-100 text-red-800' :
                                        merchant.review_status === 'in_progress' ? 'bg-amber-100 text-amber-800' :
                                        merchant.review_status === 'further_review' ? 'bg-purple-100 text-purple-800' :
                                        'bg-gray-100 text-gray-800'
                                      }
                                    >
                                      {merchant.review_status.replace('_', ' ')}
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="bg-gray-50 text-gray-600">pending</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="py-4">
                                  <div className="flex items-center justify-center gap-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-9 w-9 p-0 hover:bg-blue-100 dark:hover:bg-blue-900"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openMerchantById(merchant.id);
                                      }}
                                      title="View Details"
                                    >
                                      <Eye className="h-5 w-5 text-blue-600" />
                                    </Button>
                                    
                                    {/* Review Button */}
                                    <div onClick={(e) => e.stopPropagation()}>
                                      <ReviewButton
                                        reportId={merchant.id}
                                        merchantUrl={merchant.domain}
                                        currentStatus={merchant.review_status}
                                        onReviewComplete={() => fetchMerchants()}
                                      />
                                    </div>
                                    
                                    {/* Delete Button */}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-9 w-9 p-0 hover:bg-red-100 dark:hover:bg-red-900"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (window.confirm(`Delete all data for ${merchant.domain}? This cannot be undone.`)) {
                                          archiveMerchantData(merchant.merchant_url || merchant.domain);
                                        }
                                      }}
                                      title="Delete Merchant"
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>

                                    {/* Download Dropdown Menu */}
                                    <div onClick={(e) => e.stopPropagation()}>
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-9 w-9 p-0 hover:bg-green-100 dark:hover:bg-green-900"
                                            title="Download Report"
                                          >
                                            <Download className="h-5 w-5 text-green-600" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-48">
                                          <DropdownMenuLabel>Download Report</DropdownMenuLabel>
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem
                                            onClick={() => generateAndDownloadReport(merchant.domain, 'json')}
                                            disabled={generatingReports.has(`${merchant.domain}-json`)}
                                          >
                                            <File className="h-4 w-4 text-blue-600 mr-2" />
                                            {generatingReports.has(`${merchant.domain}-json`) ? 'Generating...' : 'JSON (Full Data)'}
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onClick={() => generateAndDownloadReport(merchant.domain, 'pdf')}
                                            disabled={generatingReports.has(`${merchant.domain}-pdf`)}
                                          >
                                            <FileText className="h-4 w-4 text-red-600 mr-2" />
                                            {generatingReports.has(`${merchant.domain}-pdf`) ? 'Generating...' : 'PDF Report'}
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            onClick={() => generateAndDownloadReport(merchant.domain, 'excel')}
                                            disabled={generatingReports.has(`${merchant.domain}-excel`)}
                                          >
                                            <FileText className="h-4 w-4 text-green-600 mr-2" />
                                            {generatingReports.has(`${merchant.domain}-excel`) ? 'Generating...' : 'CSV (Excel)'}
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}                          </TableBody>
                        </Table>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Bulk Download Section */}
                    <Card className="mt-6">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Download className="w-5 h-5 text-green-600" />
                          Bulk Report Downloads
                        </CardTitle>
                        <CardDescription>
                          Select multiple merchants to download reports in bulk for all three formats (PDF, Excel, JSON)
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {/* Selection Mode */}
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                              <input
                                type="radio"
                                id="select-current-page"
                                name="selection-mode"
                                checked={!selectAllPages}
                                onChange={() => {
                                  setSelectAllPages(false);
                                  setSelectedMerchantsForBulk([]);
                                }}
                              />
                              <label htmlFor="select-current-page" className="text-sm">
                                Select from current page ({paginatedMerchants.length} merchants)
                              </label>
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="radio"
                                id="select-all-pages"
                                name="selection-mode"
                                checked={selectAllPages}
                                onChange={() => {
                                  setSelectAllPages(true);
                                  setSelectedMerchantsForBulk([]);
                                }}
                              />
                              <label htmlFor="select-all-pages" className="text-sm">
                                Select ALL filtered merchants ({filteredMerchants.length} total)
                              </label>
                            </div>
                          </div>

                          {/* Selection Controls - Only show for current page mode */}
                          {!selectAllPages && (
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    const allMerchantIds = paginatedMerchants.map(m => m.id);
                                    setSelectedMerchantsForBulk(prev => 
                                      prev.length === allMerchantIds.length ? [] : allMerchantIds
                                    );
                                  }}
                                  className="flex items-center gap-2"
                                >
                                  {selectedMerchantsForBulk.length === paginatedMerchants.length && paginatedMerchants.length > 0 ? (
                                    <>
                                      <CheckSquare className="w-4 h-4" />
                                      Deselect All ({paginatedMerchants.length})
                                    </>
                                  ) : (
                                    <>
                                      <Square className="w-4 h-4" />
                                      Select All ({paginatedMerchants.length})
                                    </>
                                  )}
                                </Button>
                                {selectedMerchantsForBulk.length > 0 && (
                                  <span className="text-sm text-muted-foreground">
                                    {selectedMerchantsForBulk.length} merchant{selectedMerchantsForBulk.length !== 1 ? 's' : ''} selected
                                  </span>
                                )}
                              </div>
                              
                              {/* Bulk Download Buttons */}
                              {selectedMerchantsForBulk.length > 0 && (
                                <div className="flex flex-wrap items-center gap-2">
                                  {platformAdmin && !demoWorkspace && (
                                    <Button
                                      size="sm"
                                      onClick={handleMapToDemo}
                                      disabled={mappingToDemo}
                                      className={`flex items-center gap-2 ${ORANGE_BTN}`}
                                      title="Show selected reports in Paylegit Demo (database map, no copy)"
                                    >
                                      {mappingToDemo ? 'Mapping…' : `Show in Paylegit Demo (${selectedMerchantsForBulk.length})`}
                                    </Button>
                                  )}
                                  {canHideInDemo && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={handleHideFromDemo}
                                      disabled={hidingFromDemo}
                                      className="flex items-center gap-2"
                                      title="Hide selected reports from Paylegit Demo frontend"
                                    >
                                      {hidingFromDemo ? 'Hiding…' : `Hide in Demo (${selectedMerchantsForBulk.length})`}
                                    </Button>
                                  )}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => generateBulkReports('json')}
                                    disabled={bulkGenerating}
                                    className="flex items-center gap-2"
                                  >
                                    <File className="w-4 h-4 text-blue-600" />
                                    {bulkGenerating ? 'Generating...' : `JSON (${selectedMerchantsForBulk.length})`}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => generateBulkReports('pdf')}
                                    disabled={bulkGenerating}
                                    className="flex items-center gap-2"
                                  >
                                    <FileText className="w-4 h-4 text-red-600" />
                                    {bulkGenerating ? 'Generating...' : `PDF (${selectedMerchantsForBulk.length})`}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => generateBulkReports('excel')}
                                    disabled={bulkGenerating}
                                    className="flex items-center gap-2"
                                  >
                                    <FileText className="w-4 h-4 text-green-600" />
                                    {bulkGenerating ? 'Generating...' : `Excel (${selectedMerchantsForBulk.length})`}
                                  </Button>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Bulk Download Buttons for All Merchants */}
                          {selectAllPages && (
                            <div className="flex flex-wrap items-center gap-2">
                              {platformAdmin && !demoWorkspace && (
                                <Button
                                  size="sm"
                                  onClick={handleMapToDemo}
                                  disabled={mappingToDemo}
                                  className={`flex items-center gap-2 ${ORANGE_BTN}`}
                                >
                                  {mappingToDemo ? 'Mapping…' : `Show in Paylegit Demo (${filteredMerchants.length})`}
                                </Button>
                              )}
                              {canHideInDemo && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handleHideFromDemo}
                                  disabled={hidingFromDemo}
                                  className="flex items-center gap-2"
                                >
                                  {hidingFromDemo ? 'Hiding…' : `Hide in Demo (${filteredMerchants.length})`}
                                </Button>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => generateBulkReports('json')}
                                disabled={bulkGenerating}
                                className="flex items-center gap-2"
                              >
                                <File className="w-4 h-4 text-blue-600" />
                                {bulkGenerating ? 'Generating...' : `JSON (All ${filteredMerchants.length})`}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => generateBulkReports('pdf')}
                                disabled={bulkGenerating}
                                className="flex items-center gap-2"
                              >
                                <FileText className="w-4 h-4 text-red-600" />
                                {bulkGenerating ? 'Generating...' : `PDF (All ${filteredMerchants.length})`}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => generateBulkReports('excel')}
                                disabled={bulkGenerating}
                                className="flex items-center gap-2"
                              >
                                <FileText className="w-4 h-4 text-green-600" />
                                {bulkGenerating ? 'Generating...' : `Excel (All ${filteredMerchants.length})`}
                              </Button>
                            </div>
                          )}

                          {/* Selected Merchants List - Only for current page mode */}
                          {!selectAllPages && selectedMerchantsForBulk.length > 0 && (
                            <div className="border rounded-lg p-4 bg-muted/50">
                              <h4 className="text-sm font-medium mb-2">Selected Merchants:</h4>
                              <div className="flex flex-wrap gap-2">
                                {selectedMerchantsForBulk.map(merchantId => {
                                  const merchant = paginatedMerchants.find(m => m.id === merchantId);
                                  return merchant ? (
                                    <Badge 
                                      key={merchantId} 
                                      variant="secondary"
                                      className="flex items-center gap-1"
                                    >
                                      {displayMerchantName(merchant)}
                                      <button
                                        onClick={() => setSelectedMerchantsForBulk(prev => prev.filter(id => id !== merchantId))}
                                        className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </Badge>
                                  ) : null;
                                })}
                              </div>
                            </div>
                          )}

                          {/* Bulk Download Info */}
                          <div className="text-sm text-muted-foreground bg-blue-50 dark:bg-blue-950/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
                            <div className="flex items-start gap-2">
                              <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="font-medium text-blue-900 dark:text-blue-100">Bulk Download Information:</p>
                                <ul className="mt-1 space-y-1 text-blue-800 dark:text-blue-200">
                                  <li>• Select merchants using the checkboxes in the table above</li>
                                  <li>• Use "Select All" to choose all merchants on the current page</li>
                                  <li>• Download reports in PDF, Excel (CSV), or JSON format</li>
                                  <li>• Reports will be generated and downloaded as a ZIP file containing all selected merchant reports</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">
                          Showing {startIndex + 1} to {Math.min(endIndex, filteredMerchants.length)} of {filteredMerchants.length} merchants
                        </p>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                          >
                            <ChevronLeft className="w-4 h-4 mr-1" />
                            Previous
                          </Button>
                          <div className="flex items-center space-x-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }
                              return (
                                <Button
                                  key={pageNum}
                                  variant={currentPage === pageNum ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => setCurrentPage(pageNum)}
                                  className={`w-10 ${currentPage === pageNum ? ORANGE_BTN : ''}`}
                                >
                                  {pageNum}
                                </Button>
                              );
                            })}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className={ORANGE_BTN}
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                          >
                            Next
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}
        </>
      )}
      </>
      )}

      {/* Screenshot Viewer Modal */}
      {selectedScreenshot && (
        <div 
          className="fixed inset-0 z-50  flex items-center justify-center p-4"
          onClick={() => setSelectedScreenshot(null)}
        >
          <div 
            className="relative w-full h-full max-w-7xl max-h-[95vh] bg-gray-900 rounded-lg shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-black/80 border-b border-white/10">
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <Image className="w-5 h-5 text-white flex-shrink-0" />
                <span className="text-white font-medium truncate">
                  {selectedScreenshot.policy_type || selectedScreenshot.filename}
                </span>
                {selectedScreenshot.policy_type && (
                  <Badge variant="secondary" className="bg-white/20 text-white border-white/30 flex-shrink-0">
                    {selectedScreenshot.policy_type}
                  </Badge>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedScreenshot(null)}
                className="text-white hover:bg-white/20 flex-shrink-0"
              >
                ✕
              </Button>
            </div>

            {/* Info Bar */}
            <div className="flex items-center justify-between px-4 py-2 bg-black/60 text-gray-300 text-sm border-b border-white/5">
              <div className="flex items-center space-x-4">
                <span className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(selectedScreenshot.timestamp).toLocaleDateString()}</span>
                </span>
                {selectedScreenshot.page_url && (
                  <span className="flex items-center space-x-2 max-w-md truncate">
                    <Globe className="w-4 h-4" />
                    <span className="truncate">{publicDisplayUrl(selectedScreenshot.page_url)}</span>
                  </span>
                )}
              </div>
              
              {/* Zoom Controls */}
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setImageZoom(Math.max(0.5, imageZoom - 0.25))}
                  className="text-white hover:bg-white/20 h-8 px-3"
                >
                  −
                </Button>
                <span className="text-white font-mono text-sm min-w-[4rem] text-center">
                  {Math.round(imageZoom * 100)}%
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setImageZoom(Math.min(3, imageZoom + 0.25))}
                  className="text-white hover:bg-white/20 h-8 px-3"
                >
                  +
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setImageZoom(1)}
                  className="text-white hover:bg-white/20 h-8 px-3"
                >
                  Reset
                </Button>
              </div>
            </div>

            {/* Image Container */}
            <div className="flex-1 overflow-auto bg-gradient-to-b from-gray-900 to-black p-4">
              <div className="flex items-center justify-center min-h-full">
                <img
                  src={selectedScreenshot.url}
                  alt={selectedScreenshot.policy_type || 'Screenshot'}
                  className="max-w-full h-auto rounded-lg shadow-2xl transition-transform duration-200"
                  style={{ 
                    transform: `scale(${imageZoom})`,
                    imageRendering: 'crisp-edges',
                    cursor: imageZoom > 1 ? 'move' : 'default'
                  }}
                  onError={(e) => {
                    console.error('Failed to load screenshot');
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjYwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIG5vdCBhdmFpbGFibGU8L3RleHQ+PC9zdmc+';
                  }}
                />
              </div>
            </div>

            {/* Footer with shortcuts */}
            <div className="px-4 py-2 bg-black/60 border-t border-white/10 text-xs text-gray-400 flex items-center justify-between">
              <span>Click outside or press ESC to close</span>
              <span>Use +/− buttons or scroll to zoom</span>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification Stack */}
      <div className="fixed top-4 right-4 z-[99999] flex flex-col gap-2 max-w-sm pointer-events-none">
        {snackbars.map(snackbar => {
          const colorMap = {
            success: 'bg-green-50 border-green-200 text-green-800',
            error: 'bg-red-50 border-red-200 text-red-800',
            warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
            info: 'bg-blue-50 border-blue-200 text-blue-800',
          };
          const iconMap = {
            success: <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />,
            error: <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />,
            warning: <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0 mt-0.5" />,
            info: <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />,
          };
          return (
            <div
              key={snackbar.id}
              className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-lg shadow-lg border text-sm font-medium ${colorMap[snackbar.severity] || colorMap.info}`}
            >
              {iconMap[snackbar.severity] || iconMap.info}
              <span className="flex-1 leading-snug">{snackbar.message}</span>
              {snackbar.action && (
                <button
                  onClick={snackbar.action.onClick}
                  className="underline font-semibold shrink-0 hover:opacity-80"
                >
                  {snackbar.action.label}
                </button>
              )}
              <button
                onClick={() => setSnackbars(prev => prev.filter(s => s.id !== snackbar.id))}
                className="shrink-0 hover:opacity-60"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ReportsPage;